import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { fmtDate, fmtDateTime, todayISO } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import { useSearchParams } from 'react-router-dom';
import Fab from '../Fab';
import EmailTagInput from '../EmailTagInput';
import { tabColor, rowTint, menuPill, pageLine, darken, EMAIL_CAL_GROUP, pageToolbar, pageToolbarUnder, PAGE_INSET } from '../../tabPalette';
import { tint } from '../../utils/accountColors';
import { displayCompanyName, displayPersonName } from '../../utils/displayName';
import { useOfficeMailboxes } from '../../utils/officeMailboxes';   // a firm's office calendar = its office mailbox (Admin → Companies)
import { voidReasonLabel } from '../Peak/QuotationVoidDialog';   // a void's reason in the reader's language
import { openInNewTab } from '../../services/newTab';            // right-click a declined entry → its quotation in a tab
import { RC, RC_LINK } from '../../utils/fonts';

// Team calendar — Month / Week / Day views aggregating the staff CalDAV mailboxes. Each person has a colour;
// the legend toggles people. Events can be created / edited / deleted, syncing straight to the CalDAV server.

type CalAccount = { id: string; email: string; label: string; color: string; firm?: 'legal' | 'accounting' };
type CalEvent = {
  uid: string; href: string; summary: string; location: string; description: string;
  attendees: string[]; organizer: string; status: string; recurring: boolean; repeat: string; alert: string;
  date: string; time: string; endDate: string; endTime: string; allDay: boolean;
  person: string; email: string; color: string;
  guests?: string[];   // addresses added under Guests — each is emailed the invitation and the reminder
  // job-reminder overlay entries (read-only projections of job_reminders — edit them in the Job)
  source?: string; jobNo?: number; dueDate?: string; leadDays?: number; firm?: 'legal' | 'accounting'; emailTo?: string[];
  clientFull?: string;   // the job's client as stored — the title shows the name tidied (report 101b5c69)
};

// A job reminder belongs to its creator AND to its job's firm office — the entry shows while either
// chip is on (owner 2026-08-25: Legal/Accounting classification follows the JOB, not the creator).
// Quick searches (Calem 2026-09-03) — one tap lists every upcoming event of that kind.
const QUICK_SEARCHES: Array<{ label: string; fg: string; bg: string; bd: string }> = [
  { label: 'Passport',  fg: '#BE185D', bg: '#FCE7F3', bd: '#F9A8D4' },
  { label: 'ID Card',   fg: '#6D28D9', bg: '#F3E8FF', bd: '#DDD6FE' },
  { label: 'Extension', fg: '#92400E', bg: '#F6E3D3', bd: '#E7C9AC' },
  { label: 'Re-entry',  fg: '#4B5563', bg: '#F3F4F6', bd: '#D1D5DB' },
  { label: 'Permit',    fg: '#A16207', bg: '#FEF9C3', bd: '#FDE68A' },
  { label: 'License',   fg: '#15803D', bg: '#DCFCE7', bd: '#BBF7D0' },
  { label: 'Report',    fg: '#C2410C', bg: '#FFEDD5', bd: '#FED7AA' },
];
// The chips and the per-calendar show/hide pills share one row, and that row is behind the toolbar's Quick search
// button now (Calem, report 5b98cd35: "add a quick serach button where shown on the screenshot below. when clicked,
// pop-up the quick launch menu including calendar on/off for each user… when clicked agin - hide the quick search
// menu"). It was always on screen, two rows deep under the top menu. The button remembers its answer per browser,
// as the day cells' folded sections do (SEC_LS), and the row starts hidden so the calendar opens on the grid.
const QUICK_LS = 'mf_cal_quick';

// Colour events by DOCUMENT TYPE, not just by account (SLF Legal Office report 2026-09-20: "change
// the color according to the screenshot"). Reuse the exact keyword/colour table the quick-search
// chips above already use — same lowercase-substring test the server runs for those chips (see
// calendar.ts events/search `hit`) — so an event that would show up under e.g. the "Extension" quick
// search is drawn in that same colour. First match wins in QUICK_SEARCHES order. A title matching
// none of the seven categories keeps its existing account/firm colour untouched.
function categoryColorOf(summary: string): string | undefined {
  const title = (summary || '').toLowerCase();
  return QUICK_SEARCHES.find(c => title.includes(c.label.toLowerCase()))?.fg;
}
const dispColor = (e: CalEvent) => categoryColorOf(e.summary) || e.color;
// ── Day-cell sections (owner 2026-09-18) ────────────────────────────────────────────────────────
// The all-day band was drowning: 1,132 of the 1,673 events in the warm cache are birthdays (68%),
// and office@slf-legal.co alone is 73% birthdays. A 90-day report falling due sat in the middle of
// five "Happy Birthday" rows. Grouping keeps the job-related work open and folds the rest away.
//
// ⛔ These keywords are DELIBERATELY NOT the QUICK_SEARCHES list above, even though they overlap.
// Those are the toolbar's quick-search chips and the owner asked for them to stay exactly as they
// are; sharing one constant would mean every future section silently added a chip.
//
// Classification can only be done on the TITLE. 94.5% of these events are real CalDAV entries
// migrated from Google Calendar — our code never created them and the server knows nothing about
// them beyond summary + date (caldav.ts has no category field). Verified against the live cache:
// zero titles match two sections, so a first-match-wins order is unambiguous today.
//
// ⚠️ MIRRORED SERVER-SIDE in server/src/services/calendarSections.ts — the Calendar menu tab's red
// number counts the 'job' events only (Calem, report b017f89b). Change these two keywords lists
// together, or the badge will count rows that sit under a different heading here.
const BIRTHDAY_RE = /birthday|\bhbd\b|生日|วันเกิด/i;
const DOCUMENT_RE = /\bid\s*card\b|\bpassport\b/i;

// ⚠️ ANYTHING UNRECOGNISED FALLS INTO 'job', WHICH IS ALWAYS OPEN. 86 live titles match nothing at
// all, and four are corrupted ("On - ID Card (Expiry)", "ard - ID Card (Expiry)"). An extra row is
// cheap; a missed expiry is not — this register fails towards visible, never towards hidden.
type SecKey = 'job' | 'doc' | 'bday';
const sectionOf = (e: CalEvent): SecKey =>
  BIRTHDAY_RE.test(e.summary || '') ? 'bday'
  : DOCUMENT_RE.test(e.summary || '') ? 'doc'
  : 'job';

// Owner's call 2026-09-18 was that documents stay OPEN. Calem reversed it 2026-09-24: ID / Passport
// and Birthdays now sit on top and start FOLDED; Job Related sits beneath them and starts open (all
// three still fold and unfold with one tap). A folded section keeps its per-day count, so an ID card
// expiring next week is still flagged on its day — one tap away, not invisible.
const SECTIONS: Array<{ key: SecKey; labelKey: string; open: boolean }> = [
  { key: 'doc',  labelKey: 'cal_sec_doc',  open: false },
  { key: 'bday', labelKey: 'cal_sec_bday', open: false },
  { key: 'job',  labelKey: 'cal_sec_job',  open: true },
];
// _v2 (2026-09-24): the old key stores the WHOLE map on every toggle, so anyone who ever folded a
// section has documents saved as open — reading it would silently cancel the new folded default.
const SEC_LS = 'mf_cal_sections_v2';

// ── "We have already started on this one" ───────────────────────────────────────────────────────
// An expiry in the quick-search list reads as work to be done — but if the document that is expiring
// is already in our drawer, somebody has started (owner 2026-09-18). Those rows grey out so the eye
// lands on the ones nobody has picked up.
//
// MATCHED ON PERSON *AND* DOCUMENT TYPE. Person alone is not enough and the live data shows why:
// we hold Ian Baxendale's invoices and a cash payment, nothing to do with his work permit — greying
// his row would have hidden the one person on that list who most needed chasing.
export type HeldPerson = { name: string; docs: string[]; since: string };

// The document words a calendar title and a custody record actually share. Both were written by the
// same office, so the vocabulary lines up; anything outside it simply does not match and the row
// stays black — the safe direction for a list of deadlines.
const DOC_WORDS = ['work permit', 'passport', 'id card', 're-entry', 'visa', 'driving licence', 'driving license'];

/** The held entry that explains this event, or null when nobody has started. */
function heldFor(e: CalEvent, held: HeldPerson[]): HeldPerson | null {
  const title = (e.summary || '').toLowerCase();
  if (!title) return null;
  // A job reminder's title names its client tidied — titles and middle names off (report 101b5c69) —
  // while a custody record carries the name as it was typed, so the person is looked for in the title
  // and in the name as stored.
  const who = `${title} ${(e.clientFull || '').toLowerCase()}`;
  for (const h of held) {
    const nm = h.name.toLowerCase().trim();
    if (!nm || !who.includes(nm)) continue;
    // The same document, not merely the same person.
    const hit = h.docs.some(d => {
      const dl = d.toLowerCase().trim();
      const word = DOC_WORDS.find(w => dl.includes(w));
      return word ? title.includes(word) : false;
    });
    if (hit) return h;
  }
  return null;
}

// ── THE CALENDAR STRIKE-THROUGH RULE (confirmed by Calem 2026-10-28, report 28b9c313) ───────────
// ⛔ DO NOT CHANGE THIS RULE WITHOUT THE OWNER. This block is the ONE place it is written down.
// Across reports 2e266097, 8627e869 and da8763dc it was changed and partly reversed several times,
// one patch at a time, until it was put to Calem as one numbered list and he confirmed it line by
// line. jobMatchFor below is its only implementation. Every other comment about the strike (the
// grids, FUTURE_DAYS, the server's services/calendarJobMatch.ts, which only supplies the jobs)
// points here. A report that seems to want something else gets a QUESTION to the owner, not an edit.
//
// It covers only the two kinds of entry Document Storage writes, "<Client> - <Type> (Expiry)" and
// "… (Reminder — expires 14-10-2026)" (server/src/services/expiryEvents.ts; EXPIRY_RE below). A
// meeting, a court date, a birthday or any other entry is never struck. One of those two is struck
// through when the same client has a job for the same document (lines 4 and 5), going by its deadline:
//   1. Deadline TODAY OR ALREADY PAST — struck by a job that is still open, or that was closed in the
//      last 90 days (the server's WINDOW_DAYS).
//   2. Deadline 1 TO 45 DAYS AWAY (FUTURE_DAYS) — struck by a job that is still open, or that was
//      closed no more than 45 days before the deadline. ANY job counts: one typed in by hand just as
//      much as one made from a quotation or an invoice.
//   3. Deadline MORE THAN 45 DAYS AWAY — never struck, whatever job exists: nobody can be doing that
//      work yet (next quarter's 90 Day Reports stay plain).
//   4. The SAME CLIENT — the same or a very similar name (NAME_SIM), read from the title's lead or from
//      the name the reminder stores — AND the SAME DOCUMENT, never the client alone: a client's open
//      passport job must not cross out their swimming-pool licence.
//   5. The same document means the job's title names the entry's type, or the two fall in the same
//      immigration family (the "Non B Extension" filed is the "Non IMM (Business) Extension" sold —
//      typeNamed, familyOf), or the quotation or invoice line the job was sold on was dated to expire
//      on this very deadline (its Service Expiry Date, JobMatchType.expiries).
//   6. The deadline is the expiry the entry is ABOUT: an "(Expiry)" falls due on its own day, a
//      "(Reminder — expires …)" on the day written in its tail (deadlineOf). Today is the tenant's own
//      day (utils/region todayISO), never the browser's.
//
// The list was put to Calem with lines 1 and 2 as above, except that line 2 then allowed only a job
// made from a quotation or an invoice. His answer, word for word: "100% correct If the deadline is 1
// to 45 days away, the entry is struck by a job. It doesn't matter whether the job came from a
// quotation or an invoice. All jobs count. 100% correct. 100% correct. Yes. AI can ask if it is unsure
// whether the job and the calendar entry are part of the same family but generally your assessment is
// correct." So line 2 is his one correction. And when it is unclear whether a job and an entry are the
// same family (line 5), ASK, as he says; do not widen a family on a guess.
//
// How it is drawn, which the rule leaves alone: STRUCK, NEVER HIDDEN. The register fails towards
// visible (see sectionOf), and the operator must still see the entry and why it is struck (the tooltip
// names the job, jobTip). A declined quotation's light red wins over the strike (voidMatchFor). The
// title text is untouched: categoryColorOf and sectionOf classify on it, and the server's quick
// search is a substring test on it. The jobs come from the server, one small list per calendar load
// on the same request as the custody list above; all that happens here is matching them to entries.
export type JobMatchType = {
  label: string; words: string[]; active: boolean; when: string; family?: string;
  /** It came off a quotation or an invoice. ⛔ NO LONGER DECIDES ANYTHING: line 2 of the rule above,
   *  "All jobs count". The server still sends it (harmless); nothing on this screen reads it. */
  billed?: boolean;
  /** The Service Expiry Dates typed on the lines it was sold on, 'YYYY-MM-DD' (line 5 of the rule). */
  expiries?: string[];
};
export type JobMatchClient = { name: string; who: string[]; types: JobMatchType[] };
/** One immigration family, as the server ships it: the key, and the pattern that recognises that
 *  paper under either vocabulary (services/calendarJobMatch.ts jobMatchFamilies). */
export type JobMatchFamily = { key: string; match: string };
/** The same list with its word sets built once — what the screen actually compares against. */
type JobClient = { who: Set<string>; types: Array<JobMatchType & { set: Set<string> }> };
/** The families with their patterns compiled once, in the pack's order (first match wins). */
type FamilyRe = { key: string; re: RegExp };

// The two tails exactly as Document Storage writes them, and no wider: an entry somebody typed by
// hand that happens to end "(reminder)" is left alone, because a wrong strike is the one direction
// this register must not fail in.
const EXPIRY_RE = /\(\s*expiry\s*\)\s*$|\(\s*reminder\b[^)]*\bexpires\b[^)]*\)\s*$/i;

/**
 * The day an entry is a deadline FOR — which is not always the day it falls on (report 8627e869).
 *
 * A reminder names the expiry it warns about in its own tail, "(Reminder — expires 23-12-2026)",
 * DAY FIRST as expiryEvents.ts writes it (dmy) — so THAT is its deadline, which can be months after
 * the day the reminder itself sits on. A slash is read as well, for a title written by hand.
 * An "(Expiry)" expires on its own day, and so does a reminder whose tail holds no readable date:
 * the entry's own day stands, which keeps a hand-typed title behaving as it always has.
 */
const deadlineOf = (summary: string, date: string): string => {
  const own = (date || '').slice(0, 10);
  const m = /\bexpires\s+(\d{1,2})[-/](\d{1,2})[-/](\d{4})\s*\)\s*$/i.exec(summary || '');
  if (!m || Number(m[1]) > 31 || Number(m[2]) > 12) return own;
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
};

/**
 * Lines 2 and 3 of the Calendar strike-through rule (above; ⛔ not to be changed without the owner):
 * how far ahead a deadline may be and still be struck, and, for such a deadline, how long before it
 * a closed job may have been closed and still strike it. The screen's counterpart of the server's
 * WINDOW_DAYS (line 1, services/calendarJobMatch.ts), which says how far BACK a closed job still
 * answers a deadline that is already due.
 */
const FUTURE_DAYS = 45;
/** n days on from an ISO day, as an ISO day — plain date arithmetic, no browser timezone in it. */
const calAddDays = (d: string, n: number): string => {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};

// personSet and sim, the tidy and the score the server matches names by
// (server/src/services/jobArchiveMatch.ts), mirrored here because the matching happens on screen —
// keep the two in step. Titles come off through displayPersonName, this side's mirror of that rule;
// the rest is lowercase words with their accents off, so "Napiórkowski" is "Napiorkowski".
const calWords = (v: any): Set<string> => new Set(
  displayPersonName(v).normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(' ').filter(Boolean));
/** 0..1. Equal sets score 1; a subset is penalised, so "Liu" cannot pass as "Edward Liu". */
function calSim(a: Set<string>, b: Set<string>): number {
  if (!a.size || !b.size) return 0;
  const aw = Array.from(a), bw = Array.from(b);
  if (aw.slice().sort().join('') === bw.slice().sort().join('')) return 1;
  const inter = aw.filter(w => b.has(w));
  if (!inter.length) return 0;
  if (inter.length === a.size || inter.length === b.size) return inter.length / Math.max(a.size, b.size);
  return inter.length / new Set(aw.concat(bw)).size;
}
// "The same or a very very similar name" (Calem): four words in five. Short of it nothing is struck
// and the entry reads exactly as it did.
const NAME_SIM = 0.8;

// ── THE SAME PAPER UNDER TWO NAMES (report 2e266097, 3rd opening) ───────────────────────────────
// Calem named two jobs that stayed black ("Should be a job for these 61296 and 61291"). The word test
// below cannot reach them: a job is sold under the catalogue's formal name — "Non IMM (Business)
// Extension - 11 Months" — while the document that expires is filed under the short form the archive
// has always used, "Non B Extension". They share one word, "extension", so only the entries whose
// wording is identical in both vocabularies (90 Day Report, Re-entry Stamp) were ever struck.
//
// So the document type is first put through the immigration FAMILIES the server ships with the clients
// (the country pack's own vocabulary — services/calendarJobMatch.ts jobMatchFamilies). Each job type
// arrives already tagged with its family; when the expiry's type lands in the SAME one, that is the
// same paper and the entry is struck. Everything else still goes to the word test, unchanged — so a
// country with no immigration catalogue, and every document outside it (a passport, a licence, a
// chanote), behaves exactly as it did. The server sends only the families whose filing wording really
// differs from the service name (its own comment says why), so nothing the word test already answered
// is touched, and a family MISS never stops the word test from answering.
/** The family a document type or job title falls into, '' when none does. */
const familyOf = (text: string, fams: FamilyRe[]): string => {
  const txt = (text || '').trim();
  return txt ? (fams.find(f => f.re.test(txt))?.key || '') : '';
};

/**
 * Does this job's title name the document that is expiring?
 *
 * Same immigration family (above) and it is the same paper, whatever the two are called. Failing that,
 * the job's title is the longer of the two — a "Rental Accommodation (Expiry)" is answered by a
 * "Rental Accommodation License (Renewal)" job, and a "Passport (Expiry)" by "Passport (Renewal)" —
 * so the type counts as named when every word of it appears in the job's title, as well as when the
 * two score as close as two names have to. Not the other way round: a job called "Passport" says
 * nothing about a "Swimming Pool License" expiry.
 */
const typeNamed = (what: Set<string>, job: Set<string>, whatFam: string, jobFam: string): boolean =>
  (!!whatFam && whatFam === jobFam)
  || (what.size > 0 && (Array.from(what).every(w => job.has(w)) || calSim(what, job) >= NAME_SIM));

/** The job that already covers this expiry, or null when nothing in the system does: the Calendar
 *  strike-through rule above, line by line (⛔ not to be changed without the owner). */
function jobMatchFor(e: CalEvent, clients: JobClient[], fams: FamilyRe[]): JobMatchType | null {
  const summary = (e.summary || '').trim();
  if (!clients.length || !EXPIRY_RE.test(summary)) return null;
  // How far ahead the deadline is decides what may strike it (lines 1–3). The tenant's today, not
  // the browser's, and the deadline the entry NAMES (line 6, deadlineOf), so a lapsed reminder for
  // an expiry months out is judged on the expiry, not on the reminder.
  const today = todayISO();
  const deadline = deadlineOf(summary, e.date);
  const ahead = !!deadline && deadline > today;
  if (ahead && deadline > calAddDays(today, FUTURE_DAYS)) return null;   // line 3: too far out for any job
  // Line 4. "<Client> - <Document type> (Expiry)": the lead before the first " - " names the person
  // and what follows names the document. A reminder's title says the name tidied (report 101b5c69)
  // while a job row holds it as typed, so the name as stored rides along as clientFull and counts too.
  const body = summary.replace(EXPIRY_RE, '').trim();
  const cut = body.indexOf(' - ');
  const who = cut >= 0 ? calWords(body.slice(0, cut)) : new Set<string>();
  const full = calWords(e.clientFull || '');
  // The document type as the title writes it: the words for the test below, and the text itself for
  // the family, whose patterns read the spelling (brackets, hyphens) that the word tidy throws away.
  const typeText = cut >= 0 ? body.slice(cut + 3) : body;
  const what = calWords(typeText);
  if (!what.size || (!who.size && !full.size)) return null;
  const whatFam = familyOf(typeText, fams);
  for (const c of clients) {
    if (calSim(who, c.who) < NAME_SIM && calSim(full, c.who) < NAME_SIM) continue;
    const hit = c.types.find(ty =>
      // Line 1: a deadline already due takes every job in the list (open, or closed inside the
      // server's 90 days). Line 2: one ahead of us is answered by ANY job, however it was made, that
      // is still being done or was done FOR this deadline, closed no more than FUTURE_DAYS before it.
      // The sum is on the job's closing day, never the deadline's, which can come off a hand-typed
      // reminder.
      (!ahead || ty.active || (!!ty.when && calAddDays(ty.when, FUTURE_DAYS) >= deadline))
      // Line 5: the type names the document, by its wording or its family (typeNamed), or because
      // the firm itself dated that sale to expire on this very day. That date answers the
      // catalogue-name problem from the other side: a "Swimming Pool License" sold as part of a
      // package still carries the day the licence runs out, whatever it was called.
      && (typeNamed(what, ty.set, whatFam, ty.family || '')
        || (!!deadline && (ty.expiries || []).includes(deadline))));
    if (hit) return hit;
  }
  return null;
}

/** Why an entry is struck through — the job, and when it was closed if it is closed. */
const jobTip = (j: JobMatchType, t: any): string =>
  t(j.active ? 'cal_job_open_tip' : 'cal_job_done_tip', { job: j.label, d: j.when ? fmtDate(j.when) : '' });

// ── "The client has decided not to proceed" (report e4ed287f) ───────────────────────────────────
// "If there is a calendar entry and the quotation has already gone out to the client and they've
// decided not to proceed, mark that calendar entry with a light red background"; and, on how the
// system learns of the refusal, "In quotations you will see that the quotation is now void and there
// was a reason included in that void. When the user clicks on this calendar entry, it should instead
// take them directly to that quotation, explaining the void."
//
// The refusal was already recorded — the Quotations list's ⋮ Void (report 1adffffd) — and nothing on
// the Calendar read it, so a declined renewal still read as live work. The picking is the server's
// (server/src/services/calendarVoidQuotes.ts: each client's VOIDED quotation lines, with the reason
// and the Service Expiry Date typed on the line), riding in on the same one request per calendar
// load as the custody and job lists. All that happens here is deciding which entry each one answers.
//
// ⚠️ EXPIRY AND REMINDER ENTRIES ONLY, and CLIENT *AND* TYPE — the same two guards, and the same
// reasons, as jobMatchFor above. A declined pool-licence quotation must not paint a passport expiry.
//
// ⛔ THE VOID WINS OVER THE STRIKETHROUGH wherever both answer one entry: a quotation the client
// refused is not work done. The title text, the category colour, the sections and the quick searches
// are all untouched — only the fill, the words' colour and where a click goes change.
export type VoidQuoteLine = {
  label: string; words: string[]; expiry?: string;
  /** The quotation the declined line sits on, and its number for the tooltip. */
  quotationId: string; docNo: string;
  /** The void as recorded: the reason's code, its English wording, the Other words, who and when. */
  code: string; reason: string; note: string; byName: string; at: number;
};
export type VoidQuoteClient = { name: string; who: string[]; lines: VoidQuoteLine[] };
/** The same list with each line's words and family worked out once — what the screen compares. */
type VoidClient = { who: Set<string>; lines: Array<VoidQuoteLine & { set: Set<string>; family: string }> };
/** The right-click that opens a refused entry's quotation in a browser tab of its own — undefined
 *  for a reader without the Quotations page (see canQuotations). */
type VoidMenu = (v: VoidQuoteLine) => ((ev: React.MouseEvent) => void) | undefined;

/** Where a declined entry's click goes: the quotation itself, on the Quotation tab's own deep link
 *  (?open=, the one the list already reads). Never /m/quotation, which is the stub (report ab347131). */
const voidHref = (v: VoidQuoteLine): string => `/?tab=quotation&open=${encodeURIComponent(v.quotationId)}`;

/** The light red of the Void tag in the Quotations list, so the colour means the same thing here. */
const VOID_BG = '#FCEBEB';
const VOID_FG = '#B42318';

/** Why an entry is painted light red: the quotation, the reason in the reader's language, who and when. */
const voidTip = (v: VoidQuoteLine, t: any): string => t('cal_void_tip', {
  no: v.docNo || '—', reason: voidReasonLabel(t, v), name: v.byName || '—', when: v.at ? fmtDateTime(v.at) : '—',
});

/**
 * The voided quotation line that says this expiry was declined, or null when none does.
 *
 * The way in is jobMatchFor's, unchanged: the client by name (the title's lead or the reminder's
 * stored name), then the document type by its wording or its immigration family (typeNamed) — or,
 * the second way in, because the firm itself dated that very line to expire on this day (report
 * 8627e869: "you should also be able to use the expiry dates inside the quotations to match most of
 * these jobs"), which answers the catalogue-name problem from the other side.
 *
 * ⛔ NO 45-DAY WINDOW HERE, unlike the strikethrough (lines 2 and 3 of the Calendar strike-through
 * rule). That window exists because a job in hand is only evidence that a NEAR deadline is being
 * dealt with; a refusal is evidence about the deadline itself, and is just as true a year out as it
 * is tomorrow.
 *
 * ⛔ UNLESS THEY ARE PROCEEDING AFTER ALL: an ACTIVE job of the same type for that client means the
 * work is in hand whatever a past quotation says, so the entry reads exactly as it did. (Its other
 * half — a live later quotation or invoice for the same service — is the server's, where the
 * documents are; see calendarVoidQuotes.ts.) A judgement call, not in the report, and it fails in
 * the safe direction: towards the entry looking as it always has.
 */
function voidMatchFor(e: CalEvent, clients: VoidClient[], fams: FamilyRe[], jobs: JobClient[]): VoidQuoteLine | null {
  const summary = (e.summary || '').trim();
  if (!clients.length || !EXPIRY_RE.test(summary)) return null;
  const body = summary.replace(EXPIRY_RE, '').trim();
  const cut = body.indexOf(' - ');
  const who = cut >= 0 ? calWords(body.slice(0, cut)) : new Set<string>();
  const full = calWords(e.clientFull || '');
  const typeText = cut >= 0 ? body.slice(cut + 3) : body;
  const what = calWords(typeText);
  if (!what.size || (!who.size && !full.size)) return null;
  const whatFam = familyOf(typeText, fams);
  const deadline = deadlineOf(summary, e.date);
  const named = (set: Set<string>, fam: string, expiries: string[]): boolean =>
    typeNamed(what, set, whatFam, fam) || (!!deadline && expiries.includes(deadline));
  const isClient = (c: { who: Set<string> }) => calSim(who, c.who) >= NAME_SIM || calSim(full, c.who) >= NAME_SIM;
  for (const c of clients) {
    if (!isClient(c)) continue;
    const hit = c.lines.find(l => named(l.set, l.family, l.expiry ? [l.expiry] : []));
    if (!hit) continue;
    // They are going ahead after all — the entry is left as it was (and may still be struck).
    const working = jobs.some(j => isClient(j)
      && j.types.some(ty => ty.active && named(ty.set, ty.family || '', ty.expiries || [])));
    return working ? null : hit;
  }
  return null;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
// Week / Day open on 9 to 5, 09:00 at the top and 17:00 at the bottom (Calem, report eb5e4682: "Make 9 am to 5 pm
// visible"). They opened on 08:00 to 18:00 (report ae0a0d20), and under a tall all-day band ten hours could not shrink
// past PXH_MIN, so 17:00 fell off the bottom with 08:00 at the top. PXH is the tallest an hour is drawn and PXH_MIN the
// shortest; TimeGrid picks the height between them that fits the day in.
const PXH = 62;
const PXH_MIN = 40;
const DAY_START = 9, DAY_END = 17;
// The hour names sit 7px up over their line; this much room above 09:00 and below 17:00 keeps both names whole.
const LABEL_ROOM = 8;
const ALERT_KEY: Record<string, string> = { '0': 'cal_alert_attime', '10': 'cal_alert_10m', '30': 'cal_alert_30m', '60': 'cal_alert_1h', '1440': 'cal_alert_1d', '10080': 'cal_alert_1w', '20160': 'cal_alert_2w', '40320': 'cal_alert_4w', '60480': 'cal_alert_6w' };
const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Guests (Calem, report c8aa8f3f): the same address rule the server holds them to.
const GUEST_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]+$/;
/** The event is saved either way; this says when its guests could not be emailed. */
const alertGuestWarning = (r: any, t: any) => {
  const w = r?.guestWarning;
  if (w) window.alert(t(w.code === 'no_mailbox' ? 'cal_guests_nomailbox' : 'cal_guests_sendfail', { email: w.detail, why: w.detail }));
};
const mins = (t: string) => { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + m; };
const addHour = (t: string) => { const [h, m] = (t || '9:0').split(':').map(Number); return `${pad(Math.min(23, h + 1))}:${pad(m)}`; };
// The Week / Day grid's half hours, 0 (00:00–00:30) to 47 (23:30–24:00), as a time. A dragged span can end at 48, midnight,
// which <input type="time"> can't hold, so it ends at 23:59.
const slotTime = (s: number) => `${pad(Math.floor(s / 2))}:${s % 2 ? '30' : '00'}`;
const slotEnd = (s: number) => (s > 47 ? '23:59' : slotTime(s));
// A drag over a day of the Week / Day grid (report 8fcbe013, 2nd opening — see TimeGrid): the half hour it began in, the one
// the mouse is in now, whether it has left the first yet, and the day's column, which holds the mouse until it is let go.
type HourDrag = { day: number; ds: string; from: number; to: number; moved: boolean; el: HTMLElement; id: number };

// The day span a given view shows for a given cursor — pure, so the ADJACENT windows can be
// computed too (prefetch): warming the server cache with the exact ranges the arrows will request
// is what makes month/week navigation paint instantly.
function spanFor(view: 'month' | 'week' | 'day', cursor: Date): Date[] {
  if (view === 'day') return [new Date(cursor)];
  if (view === 'week') { const s = new Date(cursor); s.setDate(s.getDate() - s.getDay()); return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; }); }
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1); const s = new Date(first); s.setDate(1 - first.getDay());
  return Array.from({ length: 42 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; });
}

export default function CalendarView() {
  const { t } = useTranslation();
  const { me } = useStore();
  const [view, setView] = useState<'month' | 'week' | 'day'>('week');   // owner 2026-08-26: week is the default
  // Deep-link (owner 2026-08-25): /m/calendar?date=YYYY-MM-DD opens positioned on that date —
  // the Document-Storage wizard sends the operator here after saving an expiry-dated document.
  const [sp0] = useSearchParams();
  const [cursor, setCursor] = useState(() => {
    const d = sp0.get('date');
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd); }
    return new Date();
  });
  const [accounts, setAccounts] = useState<CalAccount[]>([]);
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // A job reminder belongs to its firm's office calendar (legal unless it says accounting).
  const offices = useOfficeMailboxes();
  const officeOfFirm = (firm?: string): string => { const f = firm || 'legal'; return f === 'accounting' ? offices.accounting : f === 'legal' ? offices.legal : ''; };
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState<CalEvent | null>(null);
  const [form, setForm] = useState<Partial<CalEvent> | null>(null);   // open create/edit form
  // Search across ALL upcoming events (Calem 2026-09-03) — a printable list, not just this month.
  const [q, setQ] = useState('');
  const [searchRes, setSearchRes] = useState<CalEvent[] | null>(null);
  // Who we are holding documents for. The list is small (twenty-odd people firm-wide) and this is
  // the only screen that consumes it.
  const [heldPeople, setHeldPeople] = useState<HeldPerson[]>([]);
  // Which clients already have a job on the go, and of what type (the Calendar strike-through rule,
  // at jobMatchFor). It comes with the custody list on the one request, which is why that request is
  // now made as the screen opens and not only when a quick search does: the grids strike their
  // entries out too, not just the search list.
  const [jobClients, setJobClients] = useState<JobMatchClient[]>([]);
  // The immigration families those job types are tagged with, so the expiry title's own document type
  // can be tagged the same way (see jobMatchFor). Country-pack vocabulary, never written on this side.
  const [jobFamilies, setJobFamilies] = useState<JobMatchFamily[]>([]);
  // Which clients have said no to a quotation, and to which services (report e4ed287f — see
  // voidMatchFor). It rides in on the very same request as the two lists above.
  const [voidClients, setVoidClients] = useState<VoidQuoteClient[]>([]);
  const loadInProgress = useCallback(() => {
    // Best-effort: if this fails, every entry simply renders as it did before.
    api.calendarInProgress()
      .then((r: any) => { setHeldPeople(r?.held || []); setJobClients(r?.jobs || []); setJobFamilies(r?.families || []); setVoidClients(r?.voids || []); })
      .catch(() => { setHeldPeople([]); setJobClients([]); setJobFamilies([]); setVoidClients([]); });
  }, []);
  useEffect(() => { loadInProgress(); }, [loadInProgress]);
  // Each client's word sets and each family's pattern, built ONCE for the whole screen rather than
  // once per entry (the grids ask the matchers for every event they draw) — and shared by both
  // matchers below, which compare against the very same two lists.
  const jobLists = useMemo(() => {
    const clients: JobClient[] = (jobClients || []).map(c => ({
      who: new Set(c.who || []),
      types: (c.types || []).map(ty => ({ ...ty, set: new Set(ty.words || []) })),
    })).filter(c => c.who.size > 0 && c.types.length > 0);
    // A pattern that will not compile is simply left out — its family then falls to the word test.
    const fams: FamilyRe[] = (jobFamilies || []).flatMap(f => {
      try { return f?.key && f?.match ? [{ key: f.key, re: new RegExp(f.match, 'i') }] : []; } catch { return []; }
    });
    return { clients, fams };
  }, [jobClients, jobFamilies]);
  const jobStruck = useMemo(() => (e: CalEvent) => jobMatchFor(e, jobLists.clients, jobLists.fams), [jobLists]);
  // The entries a client has refused (report e4ed287f — see voidMatchFor), with each declined line's
  // words and family worked out here too, and the job list to hand so work actually in progress still
  // beats a refusal that has been overtaken.
  const voidMark = useMemo(() => {
    const clients: VoidClient[] = (voidClients || []).map(c => ({
      who: new Set(c.who || []),
      lines: (c.lines || []).map(l => ({ ...l, set: new Set(l.words || []), family: familyOf(l.label, jobLists.fams) })),
    })).filter(c => c.who.size > 0 && c.lines.length > 0);
    return (e: CalEvent) => voidMatchFor(e, clients, jobLists.fams, jobLists.clients);
  }, [voidClients, jobLists]);
  // A click on a refused entry opens its quotation instead of the event panel ("When the user clicks
  // on this calendar entry, it should instead take them directly to that quotation, explaining the
  // void"). Every other entry opens the panel exactly as before — and so does a refused one for a
  // reader whose own rights do not include the Quotations page (`peak`, the permission its tab is
  // gated by), who would otherwise be sent somewhere they cannot open; the fill and its tooltip
  // still tell them it was declined. Report ab347131's rule, for the same reason it was made.
  const canQuotations = me?.role === 'admin' || (me?.modules || []).includes('peak');
  const openEvent = useCallback((e: CalEvent) => {
    const v = canQuotations ? voidMark(e) : null;
    if (v && v.quotationId) { window.location.assign(voidHref(v)); return; }
    setSel(e);
  }, [voidMark, canQuotations]);
  // And a right-click opens that quotation in a browser tab of its own, the way every other page of
  // the system is opened from a bubble or a tile (report ab347131) — at /?tab=, its only real address.
  const voidMenu = useCallback(
    (v: VoidQuoteLine) => (canQuotations ? openInNewTab(voidHref(v)) : undefined),
    [canQuotations]);
  const [searchHorizon, setSearchHorizon] = useState('');
  const [searchBusy, setSearchBusy] = useState(false);
  // Is the quick searches' row showing? Closed until the toolbar's Quick search button is pressed (report 5b98cd35 —
  // see QUICK_LS), and whichever way it was left is how it comes back.
  const [quickOpen, setQuickOpen] = useState(() => {
    try { return localStorage.getItem(QUICK_LS) === '1'; } catch { return false; }   // private window
  });
  const toggleQuick = () => setQuickOpen(o => {
    try { localStorage.setItem(QUICK_LS, o ? '0' : '1'); } catch { /* private window */ }
    return !o;
  });
  // The search box searches as you type (report 0c04879b — see the effect below), so searches can overlap: only the
  // latest may fill the list, and searchedQ holds the words it was asked for, '' with no list open.
  const searchSeq = useRef(0);
  const searchedQ = useRef('');
  const runSearch = useCallback(async (term: string) => {
    loadInProgress();   // a search can be the first thing a long-open tab does — take the fresh answer
    const tq = term.trim();
    if (!tq) return;
    const seq = ++searchSeq.current;
    searchedQ.current = tq;
    // A quick search puts its words in the box; words typed there stay as typed, a space after the last one included.
    setQ(cur => (cur.trim() === tq ? cur : tq)); setSearchBusy(true);
    try { const r: any = await api.calendarSearch(tq); if (seq !== searchSeq.current) return; setSearchRes(r.events || []); setSearchHorizon(r.horizon || ''); }
    catch { if (seq === searchSeq.current) setSearchRes([]); }
    finally { if (seq === searchSeq.current) setSearchBusy(false); }
  }, [loadInProgress]);
  const closeSearch = () => { searchSeq.current++; searchedQ.current = ''; setSearchRes(null); setSearchBusy(false); setQ(''); };
  // 300ms after the last key the box's words are searched; emptied, the box closes the list and the calendar is back
  // (Calem, report 0c04879b: "allow searching as soon as you begin typing, rather than waiting for the entire word,
  // number, or Enter"). It searched on Enter only. Enter and Esc work as they did; words Enter or a quick search has
  // already searched are not searched again.
  useEffect(() => {
    const h = setTimeout(() => {
      const tq = q.trim();
      if (tq === searchedQ.current) return;
      if (tq) runSearch(tq); else closeSearch();
    }, 300);
    return () => clearTimeout(h);
  }, [q]); // eslint-disable-line

  // Visible day span for the current view.
  const days = useMemo(() => spanFor(view, cursor), [view, cursor]);

  // ── One event, opened from the Calendar badge's pop-up (Calem, report b017f89b) ──
  // On top of the existing ?date=, a link may ask for a view (?view=day) and name an event
  // (?event=<uid>): the day the number counted, in Day view, with that event's own detail open — the
  // pop-up's row and the Calendar tab are the SAME browser tab, so this has to answer a changed
  // address, not just a fresh mount. A plain ?date= link (the Document Storage wizard's) is left
  // exactly as it was: without one of the two new params nothing here runs.
  // Applied once per link: the date and view are set the first time it is seen, and the event is
  // selected as soon as the day's events have arrived — so closing the detail leaves it closed, and
  // moving to another week does not pull it open again.
  const deepView = sp0.get('view') || '';
  const deepEvent = sp0.get('event') || '';
  const deepDate = sp0.get('date') || '';
  const deepNav = useRef('');
  const deepSel = useRef('');
  useEffect(() => {
    if (!deepView && !deepEvent) return;
    const link = `${deepDate}|${deepView}|${deepEvent}`;
    if (deepNav.current !== link) {
      deepNav.current = link;
      // Same day → the same Date object back, so the window is not re-fetched for nothing.
      if (/^\d{4}-\d{2}-\d{2}$/.test(deepDate)) {
        const [y, m, d] = deepDate.split('-').map(Number);
        setCursor(c => (ymd(c) === deepDate ? c : new Date(y, m - 1, d)));
      }
      if (deepView === 'day' || deepView === 'week' || deepView === 'month') setView(deepView);
    }
    if (!deepEvent || deepSel.current === link) return;
    const hit = events.find(e => (e.uid || '') === deepEvent);
    if (hit) { deepSel.current = link; setSel(hit); }
  }, [deepDate, deepView, deepEvent, events]);

  useEffect(() => { api.calendarAccounts().then((r: any) => setAccounts(r || [])).catch(() => {}); }, []);

  // On first load, default the visible calendars to BOTH firm offices + the user's own calendar
  // (owner 2026-08-25: "默认 office@Legal 和 office@Accounting 都选中"); everyone else starts hidden
  // (the legend can turn them back on).
  const didDefault = useRef(false);
  useEffect(() => {
    if (didDefault.current || !accounts.length || !me?.email) return;
    didDefault.current = true;
    const keep = new Set<string>(accounts.filter(a => a.email.startsWith('office@')).map(a => a.email));
    if (accounts.some(a => a.email === me.email)) keep.add(me.email);
    if (keep.size) setHidden(new Set(accounts.filter(a => !keep.has(a.email)).map(a => a.email)));
  }, [accounts, me]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r: any = await api.calendarEvents(ymd(days[0]), ymd(days[days.length - 1])); setEvents(r.events || []); }
    catch { setEvents([]); }
    finally { setLoading(false); }
    // Warm the server cache for the previous/next window (fire-and-forget, results discarded) so
    // the navigation arrows paint instantly instead of re-doing ~4s of CalDAV round trips.
    const shifted = (n: number) => { const c = new Date(cursor); if (view === 'month') c.setMonth(c.getMonth() + n); else c.setDate(c.getDate() + n * (view === 'week' ? 7 : 1)); return spanFor(view, c); };
    for (const s of [shifted(1), shifted(-1)]) void api.calendarEvents(ymd(s[0]), ymd(s[s.length - 1])).catch(() => { /* prefetch only */ });
  }, [days, view, cursor]);
  useEffect(() => { load(); }, [load]);

  const accEmails = useMemo(() => new Set(accounts.map(a => a.email)), [accounts]);
  const visible = useMemo(() => events.filter(e => e.source === 'jobReminder'
    ? !hidden.has(officeOfFirm(e.firm)) || (accEmails.has(e.email) && !hidden.has(e.email))
    : !hidden.has(e.email)), [events, hidden, accEmails, offices]);   // eslint-disable-line react-hooks/exhaustive-deps
  const byDay = useMemo(() => {
    const map: Record<string, CalEvent[]> = {};
    for (const e of visible) (map[e.date] = map[e.date] || []).push(e);
    for (const k of Object.keys(map)) map[k].sort((a, b) => (a.allDay === b.allDay ? a.time.localeCompare(b.time) : a.allDay ? -1 : 1));
    return map;
  }, [visible]);

  const toggle = (email: string) => setHidden(p => { const n = new Set(p); n.has(email) ? n.delete(email) : n.add(email); return n; });
  const shift = (n: number) => setCursor(c => { const d = new Date(c); if (view === 'month') d.setMonth(d.getMonth() + n); else d.setDate(d.getDate() + (view === 'week' ? 7 * n : n)); return d; });
  const today = ymd(new Date());

  const title = view === 'month' ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === 'day' ? `${WD[cursor.getDay()]}, ${cursor.getDate()} ${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : days[0].getMonth() === days[6].getMonth()
      ? `${days[0].getDate()} – ${days[6].getDate()} ${MONTHS[days[6].getMonth()]} ${days[6].getFullYear()}`
      : `${days[0].getDate()} ${MONTHS[days[0].getMonth()]} – ${days[6].getDate()} ${MONTHS[days[6].getMonth()]} ${days[6].getFullYear()}`;

  const reload = () => { setForm(null); setSel(null); load(); };
  const myCal = (accounts.find(a => a.email === me?.email) || accounts[0])?.email;
  // A new event starts timed, All day unticked (Calem, report 8fcbe013: "remove the all-day tick as default"): the "+"
  // opens 09:00–10:00 today, an hour of the Week / Day grid that hour, a drag over the grid's hours the time dragged (2nd
  // opening — see TimeGrid), a Month day 09:00–10:00 on that day. All day was ticked whenever no hour came with it, the
  // "+" and a Month day. An event opened to edit keeps its own (openEdit). With no end given, the form's is an hour on.
  const openNew = (date?: string, time?: string, endTime?: string) => setForm({ email: myCal, date: date || today, time: time || '', endTime: endTime || '', allDay: false, summary: '' });
  const openEdit = (e: CalEvent) => { setSel(null); setForm({ ...e }); };
  const goDay = (ds: string) => { const [y, m, d] = ds.split('-').map(Number); setCursor(new Date(y, m - 1, d)); setView('day'); };

  // The toolbar is all round bubbles in the page's purple, the top menu's Email / Contacts / Calendar box (Calem, report
  // b20498fa: "Change the menu in the screenshot below to be all circular bubbles rather than rectangular. And all
  // bubbles should be the theme color of the page."). The ‹ and › are 32px circles, and Today and Back to calendar have
  // round ends, all of them in the box's colours from menuPill, as the unselected Month / Week / Day pills are: its pale
  // wash, its line and purple words. They were 8px-cornered boxes with the faint grey outline and grey words, on the
  // app's beige (Back to calendar on nothing). The search box was round and purple already, so it is as it was.
  // Their line, and every unselected pill's and the search box's here, is a shade darker than the box's, the page's
  // (Calem, report 0fb815da: "Make the bubble border one shade darker for everything on the purple menu pages
  // (email/contacts/calendar)" — see pageLine).
  const navBtn: React.CSSProperties = { width: 32, height: 32, borderRadius: 999, border: '1px solid', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', ...menuPill('calendar', false), borderColor: pageLine('calendar') };
  // Today, Day, Week and Month, in that order, are one group of four like pills, 6px apart with nothing round them
  // (Calem, report 959a9d25: "Remove the extra border around day/week. Below and evenly spread today, then day, then
  // week, then month as a group"). The views ran Month, Week, Day 2px apart in a white tray, 3px in, with the box's
  // purple line (report b20498fa), and Today stood before the tray, bigger: 7px 13px at 13px semibold. All four wear
  // the top menu's Email / Contacts / Calendar box, as the "+" does (Calem, Bugify #667b55cb — see menuPill): the
  // selected view its solid purple #7F77DD with white words, the others its pale wash with purple words and its line.
  // Today is asked for with no view, so it is never the selected one: it moves the date, not the view. They have round
  // ends, and the 1px line comes off the padding (6px 13px → 5px 12px), so a view is as big as it was before it had
  // one. The show/hide pills on the right stay as they are, since a purple fill would hide a purple colour square on
  // them, and the quick searches keep their own colours.
  const vBtn = (v?: typeof view): React.CSSProperties => ({ padding: '5px 12px', borderRadius: 999, border: '1px solid', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', ...menuPill('calendar', view === v), ...(view !== v ? { borderColor: pageLine('calendar') } : {}) });

  // The page is white, --bg-surface (Calem, report ddf1ba0e, 3rd opening: "Change every page background to white"). It
  // was the app's beige, then for Bugify #b07c8dae's 1st opening the top menu's Email / Contacts / Calendar wash one
  // shade lighter, and for its 2nd the list's grey, --c-gray-50. The grid's days keep their own colours.
  // The toolbar's strip is the light grey, --c-gray-50, as File Storage's toolbar band is and every top-menu page's now
  // (Calem, report 3e769579, 4th opening: "Only the top menu strip on every page turns that same grey then the rest of
  // the page below this menu has a white background"). The quick searches' row under it is white, --bg-surface, the
  // page's own (Calem, report d516ca7b, 2nd opening: "white background", written on that row): it had been given the
  // toolbar's grey as well, so the grey ran two rows deep. Before report 3e769579 neither row had a colour of its own,
  // so both were the page's, white since report ddf1ba0e. Each kept its line under it, a shade darker, --border-strong
  // where it was --border, as is the top menu's over the toolbar (Calem, report a903bda5, 3rd opening: "Make the gray
  // horizontal lines one shade darker on all page menus like this").
  // Both rows are bubbles now, as every page toolbar is (Calem, report 76a2c198, 2nd opening — see pageToolbar): 10px
  // corners and a --border-strong line all the way round each, in place of the line that ran under it, PAGE_INSET in
  // from the page's edges so the page's white runs down both sides of them — the one page gutter the whole system
  // stands in by, 15px (that report's 5th opening: "Move the left and right page spacing in from the edges to 15 px on
  // every page of the SLF system"). The toolbar gives the gap above itself and one
  // below; the quick searches stack under it (pageToolbarUnder), so one gap parts the two rows and another parts the
  // second from the grid. Their fills — grey on the toolbar, white on the quick searches — are as they were.
  // The Month grid, the Day and Week grids and the search results all stand in by that same 15 below them, so the
  // columns, the cards and the bubbles end on the one line (see `side` in TimeGrid and the two grids' padding).
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-surface)', position: 'relative' }}>
      {/* Prominent loading banner (calendars load over the network) */}
      {loading && (
        <div style={{ position: 'absolute', top: 64, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 30, pointerEvents: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: '#FCE7F3', color: '#DB2777', fontWeight: 800, fontSize: 15.5, padding: '11px 22px', borderRadius: 999, boxShadow: '0 6px 20px rgba(219,39,119,0.25)', border: '1px solid #F9A8D4' }}>
            <i className="ti ti-loader-2" style={{ animation: 'spin 0.9s linear infinite' }} /> {t('cal_loading_full')}
          </div>
        </div>
      )}
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', flexWrap: 'wrap', ...pageToolbar(), background: 'var(--c-gray-50)' }}>
        <button style={navBtn} onClick={() => shift(-1)}><i className="ti ti-chevron-left" /></button>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', minWidth: 200 }}>{title}</div>
        <button style={navBtn} onClick={() => shift(1)}><i className="ti ti-chevron-right" /></button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button style={vBtn()} onClick={() => setCursor(new Date())}>{t('cal_today')}</button>
          <button style={vBtn('day')} onClick={() => setView('day')}>{t('cal_day')}</button>
          <button style={vBtn('week')} onClick={() => setView('week')}>{t('cal_week')}</button>
          <button style={vBtn('month')} onClick={() => setView('month')}>{t('cal_month')}</button>
        </div>
        {loading && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('cal_loading')}</span>}
        {/* The search box, with its spinner and Back to calendar, sits at the right end of the toolbar, the loading words
            before it, and the show/hide pills are one row down, at the right end of the quick searches' row (Calem, report
            d516ca7b: "Bring the users' calendars down one row and then right-align the search bar"). The pills were on
            this row, after the search box and pushed to the row's end, which left the search box in the middle of it. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          {/* Quick search — the button that shows and hides the row under this one, standing immediately before the
              search box, where Calem drew it (report 5b98cd35 — see QUICK_LS). It is a Day / Week / Month pill in
              every respect (vBtn): the same round ends, 5px 12px and 12.5px bold, the box's purple #7F77DD with white
              words while the row is showing and its pale wash with purple words and its line while it is hidden — so
              it reads as pressed exactly when the row is on screen. A magnifier before the words and a chevron after
              them, pointing up while the row is open and down while it is closed. */}
          <button onClick={toggleQuick} title={t('cal_quick')} aria-expanded={quickOpen}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999, border: '1px solid', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', ...menuPill('calendar', quickOpen), ...(quickOpen ? {} : { borderColor: pageLine('calendar') }) }}>
            <i className="ti ti-search" style={{ fontSize: 13 }} aria-hidden />
            {t('cal_quick')}
            <i className={quickOpen ? 'ti ti-chevron-up' : 'ti ti-chevron-down'} style={{ fontSize: 12 }} aria-hidden />
          </button>
          {/* The search box is a round bubble in the top menu's Email / Contacts / Calendar purple (Calem, Bugify
              #cba01676, 6th opening: "Do the same (colored bubble search) for quotation/invoice/receipts,
              email/calendar and petty cash"), dressed as Bank Statement's search box: round ends, a 1px line, 5px 12px
              and 12px semibold, in the box's colours from menuPill — the Month / Week / Day pills' own, unselected: its
              pale wash, its line and purple words, the placeholder's too (mf-ph-inherit) — with the magnifier inside it,
              in the purple 12px in, and the words 30px in, clear of it. The magnifier stood outside, grey and 15px, 6px
              before the box, so the box takes that room: 200px, where it was 180, keeps the row as it was and the words
              as much room as they had. Enter and Esc work as they did. It was an 8px-cornered box with the faint grey
              outline, its words 13px. It searches as you type as well now (report 0c04879b — see runSearch).
              It is Filing's search box's size now and reads "Search calendar…", where it read "Search all upcoming
              events…" (Calem, report 48c2197c, 2nd opening: "Do the same for every search bar. Put the name inside"):
              13.5px words where they were 12, 7px above and below them where it was 5, the magnifier 14.5px and 16px
              in, the words 36px in and 16px clear of the right-hand end, 220px wide where it was 200 so they fit. It
              reaches 3px into the toolbar's padding above and below, so it takes the room it took. */}
          <div style={{ position: 'relative', margin: '-3px 0' }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 14.5, color: tabColor('calendar') }} aria-hidden />
            <input value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') runSearch(q); if (e.key === 'Escape') closeSearch(); }}
              placeholder={t('cal_search_calendar')} className="mf-ph-inherit"
              style={{ width: 220, padding: '7px 16px 7px 36px', fontSize: 13.5, fontWeight: 600, borderRadius: 999, border: '1px solid', outline: 'none', ...menuPill('calendar', false), borderColor: pageLine('calendar') }} />
          </div>
          {searchBusy && <i className="ti ti-loader-2" style={{ animation: 'spin 0.9s linear infinite', color: 'var(--text-tertiary)' }} aria-hidden />}
          {searchRes !== null && !searchBusy && (
            <button onClick={closeSearch} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', ...menuPill('calendar', false), borderColor: pageLine('calendar') }}>{t('cal_search_close')}</button>
          )}
        </div>
      </div>

      {/* Quick searches (Calem 2026-09-03): one tap lists every upcoming related event. On screen only while the
          toolbar's Quick search button is pressed (report 5b98cd35 — see QUICK_LS); hidden, nothing else moves,
          because this row carries no gap above it (pageToolbarUnder) and the toolbar's bubble gives the one
          TOOLBAR_GAP under itself either way. Its contents, its colours and its bubble are untouched. */}
      {quickOpen && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', flexWrap: 'wrap', ...pageToolbarUnder(), background: 'var(--bg-surface)' }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.03em' }}>{t('cal_quick')}</span>
        {QUICK_SEARCHES.map(c => (
          <button key={c.label} onClick={() => runSearch(c.label)}
            style={{ padding: '4px 12px', borderRadius: 999, border: `1px solid ${c.bd}`, background: c.bg, color: c.fg, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
            {c.label}
          </button>
        ))}
        {/* The show/hide pills, one per calendar, at the right end of this row (Calem, report d516ca7b — see the search
            box). They were on the toolbar's row, to the right of the search box. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginLeft: 'auto' }}>
          {accounts.map(a => {
            const on = !hidden.has(a.email);
            return (
              <button key={a.email} onClick={() => toggle(a.email)} title={a.email}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, border: '1px solid var(--border)', background: on ? 'var(--bg-surface)' : 'transparent', color: on ? 'var(--text-primary)' : 'var(--text-tertiary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: on ? 1 : 0.5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: a.color }} />{a.label}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {searchRes !== null
        ? <SearchList q={q} events={searchRes.filter(e => e.source === 'jobReminder'
            ? !hidden.has(officeOfFirm(e.firm)) || (accEmails.has(e.email) && !hidden.has(e.email))
            : !hidden.has(e.email))} horizon={searchHorizon} held={heldPeople} struck={jobStruck} refused={voidMark} voidMenu={voidMenu} t={t} onEvent={openEvent} onClose={closeSearch} />
        : view === 'month'
        ? <MonthGrid days={days} byDay={byDay} curMonth={cursor.getMonth()} today={today} struck={jobStruck} refused={voidMark} voidMenu={voidMenu} t={t} onEvent={openEvent} onNew={d => openNew(d)} onDay={goDay} />
        : <TimeGrid days={days} byDay={byDay} today={today} struck={jobStruck} refused={voidMark} voidMenu={voidMenu} t={t} onEvent={openEvent} onNew={(d, tm, endTm) => openNew(d, tm, endTm)} onDay={goDay} single={view === 'day'} />}

      {sel && <DetailModal e={sel} t={t} onClose={() => setSel(null)} onEdit={() => openEdit(sel)} onDeleted={reload} />}
      <Fab onClick={() => openNew()} title={t('cal_new_event')} color={tabColor('calendar')} />
      {form && <EventForm initial={form} accounts={accounts} t={t} onClose={() => setForm(null)} onSaved={reload} />}
    </div>
  );
}

// ── Search results: every upcoming event, grouped by date, printable (Calem 2026-09-03) ─────────
// Print opens a self-contained window (inline styles only) — the app shell, its 100vh clipping and
// the sidebar never reach the paper.
function SearchList({ q, events, horizon, held, struck, refused, voidMenu, t, onEvent, onClose }: {
  q: string; events: CalEvent[]; horizon: string; held: HeldPerson[]; struck: (e: CalEvent) => JobMatchType | null;
  refused: (e: CalEvent) => VoidQuoteLine | null; voidMenu: VoidMenu;
  t: any; onEvent: (e: CalEvent) => void; onClose: () => void;
}) {
  const byDay = useMemo(() => {
    const m: Record<string, CalEvent[]> = {};
    for (const e of events) (m[e.date] = m[e.date] || []).push(e);
    for (const k of Object.keys(m)) m[k].sort((a, b) => (a.allDay === b.allDay ? (a.time || '').localeCompare(b.time || '') : a.allDay ? -1 : 1));
    return m;
  }, [events]);
  const dates = Object.keys(byDay).sort();
  // Each event row in the top menu's Calendar purple, two shades lighter (Calem, Bugify #1a85b4b0 — see rowTint), not
  // transparent. Hover is still --bg-active; an event already in hand is still dimmed, not recoloured.
  const rowBg = rowTint('calendar')!;
  const fmtD = (d: string) => { const x = new Date(d + 'T00:00:00'); return `${WD[x.getDay()]}, ${x.getDate()} ${MONTHS[x.getMonth()]} ${x.getFullYear()}`; };
  const print = () => {
    const esc = (v: any) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const rows = dates.map(d => `
      <h3 style="margin:18px 0 6px;font-size:14px;border-bottom:1px solid #ccc;padding-bottom:3px">${esc(fmtD(d))}</h3>
      <table style="border-collapse:collapse;width:100%">${byDay[d].map(e => {
        // A refused entry prints on the Void tag's light red, in its red, and is NOT crossed out:
        // the client said no, which is not the same as the work being done (report e4ed287f).
        const vd = refused(e);
        return `
        <tr${vd ? ` style="background:${VOID_BG}"` : ''}>
          <td style="padding:3px 14px 3px 0;white-space:nowrap;color:#555;width:70px;vertical-align:top">${e.allDay ? esc(t('cal_all_day')) : esc(e.time || '')}</td>
          <td style="padding:3px 14px 3px 0;font-weight:600${vd ? `;color:${VOID_FG}` : ''}"><span${!vd && struck(e) ? ' style="text-decoration:line-through"' : ''}>${esc(e.summary)}</span>${heldFor(e, held) ? ' <span style="font-weight:600;color:#0F6B49">\u2713 ' + esc(t('cal_inprog')) + '</span>' : ''}</td>
          <td style="padding:3px 14px 3px 0;color:#555;white-space:nowrap">${esc(e.person || e.email || '')}</td>
          <td style="padding:3px 0;color:#777">${esc(e.location || '')}</td>
        </tr>`;
      }).join('')}</table>`).join('');
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(q)}</title>${RC_LINK}</head>
      <body style="font-family:${RC};color:#111;padding:24px;font-size:13px">
      <h2 style="margin:0 0 2px">${esc(t('cal_search_results', { n: events.length }))} — “${esc(q)}”</h2>
      <div style="color:#666;font-size:12px">${esc(fmtDateTime(new Date()))}${horizon ? ` · ${esc(t('cal_search_horizon', { d: horizon }))}` : ''}</div>
      ${rows || `<p>${esc(t('cal_search_none'))}</p>`}
      </body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: `14px ${PAGE_INSET}px` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 15, color: 'var(--text-primary)' }}>{t('cal_search_results', { n: events.length })} — “{q}”</b>
        {horizon && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('cal_search_horizon', { d: horizon })}</span>}
        <span style={{ flex: 1 }} />
        <button onClick={print} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}><i className="ti ti-printer" aria-hidden />{t('cal_print')}</button>
        <button onClick={onClose} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}>{t('cal_search_close')}</button>
      </div>
      {dates.length === 0 && <div style={{ color: 'var(--text-tertiary)', padding: 30, textAlign: 'center' }}>{t('cal_search_none')}</div>}
      {dates.map(d => (
        <div key={d} style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, fontSize: 13.5, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', padding: '4px 0', marginBottom: 4 }}>{fmtD(d)}</div>
          {byDay[d].map((e, i) => {
            // Already in hand → dim the whole row, but never hide it: the operator must still be
            // able to see it and check why it was dimmed. An expiry that already has a job on it is
            // struck through instead (the strike-through rule at jobMatchFor); the two can both be true.
            const h = heldFor(e, held);
            const j = struck(e);
            // The client has refused this one (report e4ed287f): the row takes the Void tag's light
            // red, its words go the tag's red and the strikethrough stands down — a refusal is not
            // work done. The fill stays put under the mouse, so hover still only lightens the rest.
            const vd = refused(e);
            const bg = vd ? VOID_BG : rowBg;
            return (
            <div key={(e.uid || '') + i} onClick={() => onEvent(e)} onContextMenu={vd ? voidMenu(vd) : undefined}
              title={[vd ? voidTip(vd, t) : '', h ? t('cal_inprog_tip', { docs: h.docs.join(', '), since: h.since }) : '', !vd && j ? jobTip(j, t) : ''].filter(Boolean).join('\n') || undefined}
              style={{ display: 'flex', gap: 14, alignItems: 'baseline', padding: '5px 8px', borderRadius: 7, cursor: 'pointer', opacity: h ? 0.45 : 1, background: bg }}
              onMouseEnter={ev => (ev.currentTarget.style.background = vd ? VOID_BG : 'var(--bg-active)')}
              onMouseLeave={ev => (ev.currentTarget.style.background = bg)}>
              <span style={{ width: 62, flexShrink: 0, fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{e.allDay ? t('cal_all_day') : (e.time || '')}</span>
              <span style={{ fontWeight: 600, fontSize: 13.5, color: vd ? VOID_FG : 'var(--text-primary)', textDecoration: !vd && j ? 'line-through' : undefined }}>{e.summary}</span>
              {h && <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 800, color: '#0F6B49', background: '#E6F6EF', border: '1px solid #B9E5D2', borderRadius: 999, padding: '0 8px', whiteSpace: 'nowrap' }}>{t('cal_inprog')}</span>}
              <span style={{ fontSize: 12, color: dispColor(e) || 'var(--text-tertiary)' }}>{e.person || e.email}</span>
              {e.location && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{e.location}</span>}
            </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function MonthGrid({ days, byDay, curMonth, today, struck, refused, voidMenu, t, onEvent, onNew, onDay }: { days: Date[]; byDay: Record<string, CalEvent[]>; curMonth: number; today: string; struck: (e: CalEvent) => JobMatchType | null; refused: (e: CalEvent) => VoidQuoteLine | null; voidMenu: VoidMenu; t: any; onEvent: (e: CalEvent) => void; onNew: (d: string) => void; onDay: (d: string) => void }) {
  const CAP = 8;
  // When the month grid mounts / its content settles, scroll so today's row sits just under the sticky
  // weekday header (the grid can be very tall on busy days). If today isn't in this month, scroll to top.
  const scrollRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const cont = scrollRef.current; if (!cont) return;
    const id = requestAnimationFrame(() => {
      const cell = todayRef.current;
      if (cell) cont.scrollTop += cell.getBoundingClientRect().top - cont.getBoundingClientRect().top - 42;
      else cont.scrollTop = 0;
    });
    return () => cancelAnimationFrame(id);
  }, [days, byDay]);
  // The day names and the days are two separate grids, so both take this one template, and its tracks
  // are minmax(0, 1fr), never a bare 1fr: a 1fr column can't shrink below its content, so a day holding
  // a long event widened its own column below but not the name above it, and the names slid off the
  // column borders by an amount that changed with the zoom (Calem, report 257a251e: at 80% TUE ran
  // under WED). The day cell's minWidth: 0 keeps its pills inside the column; a long title is cut short.
  const cols = 'repeat(7, minmax(0, 1fr))';
  // The day names and the days stand PAGE_INSET in from the page's left and right, the one page gutter the whole
  // system stands in by, where this grid alone ran edge to edge (Calem, report 76a2c198, 5th opening: "Move the left
  // and right page spacing in from the edges to 15 px on every page of the SLF system"), so the week's first and last
  // column lines stand under the toolbar bubble's ends. It goes on both grids and not on the scroll box, as the Day
  // and Week grids take it (`side` in TimeGrid): the names row's white and its line still run edge to edge, and the
  // names stay centred over the day columns because both grids are padded by the same amount.
  return (
    <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: cols, position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)', padding: `0 ${PAGE_INSET}px` }}>
        {WD.map(w => <div key={w} style={{ padding: '8px', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-tertiary)', textAlign: 'center' }}>{w}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gridAutoRows: 'minmax(144px, auto)', padding: `0 ${PAGE_INSET}px` }}>
        {days.map((d, i) => {
          const ds = ymd(d); const evs = byDay[ds] || []; const inMonth = d.getMonth() === curMonth; const isToday = ds === today;
            // Birthdays are pulled out of the cap entirely; the rest keeps its original order so a
            // day still reads chronologically. 68% of all events are birthdays — leaving them in the
            // slice is what made the month grid useless for spotting real work.
            const bdays = evs.filter(e => sectionOf(e) === 'bday');
            const rest = evs.filter(e => sectionOf(e) !== 'bday');
            const shown = rest.slice(0, CAP);
          // One click on a day opens New event on it (Calem, report 8fcbe013: "Allow users to click on the time block to
          // create a new event"); it took a double-click. A click on a button the day holds — an event, the day's
          // number, the birthdays line, "+N more" — does that button's job and nothing else.
          return (
            <div key={i} ref={isToday ? todayRef : undefined} onClick={ev => { if (!(ev.target as Element).closest('button')) onNew(ds); }} style={{ borderRight: (i % 7 !== 6) ? '1px solid var(--border)' : 'none', borderBottom: '1px solid var(--border)', padding: 6, background: inMonth ? 'var(--bg-surface)' : 'var(--bg-app)', display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              {/* The day's number, today's on a circle: the figures trimmed to their cap height, so the flex box centres
                  the digits themselves, as the top menu's count bubbles do (Bugify #836d4443, 2nd opening: "Check all
                  other circles in the system and make sure of the same - centered vertically and horizontally").
                  The circle is the Calendar's purple, its top-menu button's colour (tabColor), where it was the app's
                  green accent (Calem, report 2f61c80a, 2nd opening: "Use the calendar theme color (purple) for the
                  selected day"); the Week / Day header's matches. */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                <button onClick={() => onDay(ds)} title={t('cal_day')} style={{ border: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: isToday ? 800 : 600, color: isToday ? '#fff' : inMonth ? 'var(--text-secondary)' : 'var(--text-tertiary)', background: isToday ? tabColor('calendar') : 'transparent', minWidth: 26, height: 26, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 7px' }}>
                  <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{d.getDate()}</span>
                </button>
              </div>
              {shown.map((e, j) => {
                const c = dispColor(e);
                // An expiry that already has a job on it is drawn struck through (the strike-through
                // rule at jobMatchFor). The pill keeps its colour, its size and its place; only the
                // words are crossed, and the tooltip says which job it is.
                const done = struck(e);
                // The client has refused this one (report e4ed287f): the pill's wash becomes the Void
                // tag's light red and its words the tag's red, and the strikethrough stands down. The
                // pill keeps its size, its place and its 3px colour bar, so the category still reads.
                const vd = refused(e);
                // All-day pills run 4px lower than timed ones so more fit in a cell — height only, the
                // width and text size are untouched — and write their title in the event's own colour,
                // e.g. Extension orange (Calem 2026-09-24). The Week/Day band's all-day pills were this
                // same pill until report 17b67403 took their wash and their 3px left bar away and gave
                // them the colour as a line all round instead; these keep both, because that report is
                // about the band — a month cell stacks its pills from the top edge down and has no
                // "provided space" round them, so there is nothing here for the outline to sit in.
                return (
                  <button key={j} onClick={() => onEvent(e)} onContextMenu={vd ? voidMenu(vd) : undefined}
                    title={`${e.person}: ${e.summary}${vd ? `\n${voidTip(vd, t)}` : done ? `\n${jobTip(done, t)}` : ''}`}
                    style={{ flexShrink: 0, display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'left', border: 'none', borderRadius: 5, padding: e.allDay ? '3px 10px' : '5px 10px', background: vd ? VOID_BG : c + '22', color: vd ? VOID_FG : e.allDay ? c : 'var(--text-primary)', cursor: 'pointer', fontSize: 13, lineHeight: 1.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', borderLeft: `3px solid ${c}`, textDecoration: !vd && done ? 'line-through' : undefined }}>
                    {e.source === 'jobReminder' && <i className="ti ti-bell-ringing" style={{ color: c, marginRight: 4 }} />}{!e.allDay && <b style={{ color: c, marginRight: 4 }}>{e.time}</b>}{e.summary}
                  </button>
                );
              })}
              {/* Birthdays never compete for the eight slots — they fold into one line that opens
                  the day. Before this, six birthdays ate six of the eight and pushed the expiry
                  that actually needed doing out of sight (owner 2026-09-18). */}
              {bdays.length > 0 && <button onClick={() => onDay(ds)} style={{ flexShrink: 0, alignSelf: 'flex-start', border: '1px dashed var(--border-strong)', borderRadius: 999, background: 'transparent', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', cursor: 'pointer', padding: '1px 9px', whiteSpace: 'nowrap' }}>{t('cal_sec_bday_n', { n: bdays.length })}</button>}
              {rest.length > shown.length && <button onClick={() => onDay(ds)} style={{ flexShrink: 0, border: 'none', background: 'transparent', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--accent)', cursor: 'pointer', paddingLeft: 7 }}>{t('cal_more', { n: rest.length - shown.length })}</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimeGrid({ days, byDay, today, struck, refused, voidMenu, t, onEvent, onNew, onDay, single }: { days: Date[]; byDay: Record<string, CalEvent[]>; today: string; struck: (e: CalEvent) => JobMatchType | null; refused: (e: CalEvent) => VoidQuoteLine | null; voidMenu: VoidMenu; t: any; onEvent: (e: CalEvent) => void; onNew: (d: string, tm: string, endTm?: string) => void; onDay: (d: string) => void; single?: boolean }) {
  const allDayByDay = days.map(d => (byDay[ymd(d)] || []).filter(e => e.allDay));
  const timedByDay = days.map(d => (byDay[ymd(d)] || []).filter(e => !e.allDay));
  // Room for the all-day band's section labels (Calem 2026-09-24: 64px cut 'ID / Passport' and
  // 'Birthdays' short). The day header and the hour column share it, or the day columns would no
  // longer line up from the header down through the band into the time grid.
  const gutter = 96;
  // Those are separate grids, so they share this one template — day tracks minmax(0, 1fr), never a
  // bare 1fr, which can't shrink below its content and so could widen one grid's column and not the
  // others' (report 257a251e — see MonthGrid).
  const cols = `${gutter}px repeat(${days.length}, minmax(0, 1fr))`;
  // The all-day section cards stand this far in from the page's left and right: PAGE_INSET, the one page gutter the
  // whole system stands in by (Calem, report 76a2c198, 5th opening: "Move the left and right page spacing in from the
  // edges to 15 px on every page of the SLF system"). It was this screen's own — 7 (Calem 2026-09-30, report
  // 0b462a24: "Bring these bubbles in 7 pixels from either side of the page, left and right"), then 12 (report
  // efa40753: "Bring these bubbles in another 5 pixels from the edge of the page, left and right") — so the cards
  // come in 3px further at each end and now end where the toolbar bubble above them does. The
  // day header and the time grid are padded by the same amount, or the cards' days would be narrower than theirs
  // and slide off them (report 257a251e). It is padding, so the header's white and the band's grey still run edge
  // to edge; only the columns move in.
  const side = PAGE_INSET;
  // The day names' row holds each day's name and date on one line, centred over the day ('SUN 4'), where the name sat
  // on a line of its own over the date (report 2f61c80a: "bring the day of the week and the number up onto the same
  // line to save vertical space"); the name and the date are both centred on the row's middle, 4px apart. The row is
  // the date's 30px circle, the day's 5px padding over and under it and its 1px line beneath: 41px, where it was 62.
  // The all-day band sticks this far down, so it starts where the row ends — no gap, no overlap.
  const headH = 41;
  // The cards' purple line is four shades darker than the top menu box's (Calem 2026-09-30, report 0b462a24: "make
  // the purple border one shade darker"): 0.33 → 0.50, a quarter of the way to the solid #7F77DD, the step the
  // Quotation, Bank and Jobs borders took (see tabPalette); then report efa40753, 2nd opening: "Make the purple border
  // around these bubbles one shade darker": 0.50 → 0.625, a quarter of the way again from there to the solid purple,
  // so on white the line goes #D5D2F4 → #BFBBEE → #AFAAEA; then report ddf1ba0e, 3rd opening: "darken up the bubble
  // border on each page by one shade": 0.625 → 0.72, a quarter of the way on once more (0.71875, to two places as the
  // other pages' lines are — see darken), #A39DE7 on white; then report 0fb815da: "Make the bubble border one shade
  // darker for everything on the purple menu pages (email/contacts/calendar)": 0.72 → 0.79, a quarter of the way on
  // again, #9A94E4 on white — the hour's bubble with them. The toolbar's ‹ › and unselected Today / Day / Week / Month
  // pills and the search box took the box's line a shade darker then, 0.50 (pageLine), and the folded sections' counts
  // a shade on from that, 0.63 (see the count); the box itself and Tracking's Email chain button keep
  // EMAIL_CAL_GROUP.border. The Contacts cards are a step behind (report 13ecfa12, CARD_LINE there), at 0.72.
  const cardLine = tint(EMAIL_CAL_GROUP.color, 0.79);

  // Collapse is per SECTION and shared by every column — never per day. The band is one CSS grid;
  // letting Monday expand while Tuesday stays folded would tear the columns out of alignment.
  const [openSec, setOpenSec] = useState<Record<string, boolean>>(() => {
    const d = Object.fromEntries(SECTIONS.map(x => [x.key, x.open]));
    try { return { ...d, ...JSON.parse(localStorage.getItem(SEC_LS) || '{}') }; } catch { return d; }
  });
  const toggleSec = (k: SecKey) => setOpenSec(p => {
    const n = { ...p, [k]: !p[k] };
    try { localStorage.setItem(SEC_LS, JSON.stringify(n)); } catch { /* private window */ }
    return n;
  });
  // A section with nothing in it anywhere this week is not drawn at all — which is also why this
  // change is invisible on a calendar that has no birthdays (the client portal mounts the same view).
  const secRows = SECTIONS.map(sec => {
    const perDay = allDayByDay.map(list => list.filter(e => sectionOf(e) === sec.key));
    return { ...sec, perDay, total: perDay.reduce((n, a) => n + a.length, 0) };
  }).filter(r => r.total > 0);

  // The grid opens on 9 to 5 (Calem, report eb5e4682: "Make 9 am to 5 pm visible"). It opened on 8 to 6 (report
  // ae0a0d20: "Default: show 8 am till 6 pm, which is the working day and the user can scroll up or down from there"),
  // and before that drew all 24 hours at 62px and always opened at midnight. All 24 are still there to scroll to, but
  // an hour is as tall as lets 09:00 to 17:00 fill the room under the day names and the all-day band — never under
  // PXH_MIN, never over the 62px it was — and the grid scrolls so 09:00 sits just under the band when it opens and
  // whenever the days it shows change (another week or day, Week ↔ Day). That room is measured on screen: the band is
  // as tall as its busiest day, grows when the week's events come in and changes as sections fold, so it is watched,
  // as the box is for the window, and the hour height follows it. Until the user scrolls, 09:00 is kept under the band
  // through all that; once they have, the grid never scrolls them anywhere — when the hours change height it keeps the
  // hour they have at the top where it is. Eight hours share the room where ten did, so each is taller: with Birthdays
  // and Job Related both in the band, ten hours were already down to PXH_MIN and 17:00 still ran off the bottom. Only
  // a room under eight PXH_MINs, 320px, still puts 17:00 past the edge (worked out, not measured on screen).
  const boxRef = useRef<HTMLDivElement>(null);
  const bandRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLDivElement>(null);   // the first day's 09:00 row
  const [pxh, setPxh] = useState(PXH);
  const drawnPxh = useRef(PXH);                    // the height the hours have on screen now
  const pinned = useRef(true);                     // the user has not scrolled since these days came up
  const lastTop = useRef(0);                       // where the grid itself last put the scroll
  const topHour = useRef(DAY_START);               // the hour the user had at the top when the heights last changed
  const setScroll = useCallback((top: number) => {
    const box = boxRef.current;
    if (!box) return;
    box.scrollTop = top;
    lastTop.current = box.scrollTop;   // read back: the box can't always scroll as far as asked
  }, []);
  const toDayStart = useCallback(() => {
    const box = boxRef.current, band = bandRef.current, row = startRef.current;
    if (box && band && row) setScroll(box.scrollTop + row.getBoundingClientRect().top - band.getBoundingClientRect().bottom - LABEL_ROOM);
  }, [setScroll]);
  const measure = useCallback(() => {
    const box = boxRef.current, band = bandRef.current;
    if (!box || !band) return;
    const room = box.clientHeight - (band.getBoundingClientRect().bottom - box.getBoundingClientRect().top) - 2 * LABEL_ROOM;
    const next = Math.min(PXH, Math.max(PXH_MIN, Math.floor(room / (DAY_END - DAY_START))));
    if (pinned.current) toDayStart();
    else if (next !== drawnPxh.current) topHour.current = (box.scrollTop + LABEL_ROOM) / drawnPxh.current;
    setPxh(next);
  }, [toDayStart]);
  useLayoutEffect(() => {
    const box = boxRef.current, band = bandRef.current;
    if (!box || !band) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box); ro.observe(band);
    return () => ro.disconnect();
  }, [measure]);
  useLayoutEffect(() => {
    drawnPxh.current = pxh;
    if (pinned.current) toDayStart(); else setScroll(topHour.current * pxh - LABEL_ROOM);
  }, [pxh, toDayStart, setScroll]);
  const span = `${ymd(days[0])}/${days.length}`;
  useLayoutEffect(() => { pinned.current = true; toDayStart(); }, [span, toDayStart]);
  // Whatever scrolls the grid to somewhere it did not put itself — wheel, keys, scrollbar, finger — is the user.
  const onScroll = () => {
    const box = boxRef.current;
    if (box && Math.abs(box.scrollTop - lastTop.current) > 1) pinned.current = false;
    setHover(null);   // the hours moved under the mouse; its next move puts the hour bubble back (see below)
  };

  // The hour under the mouse shows as a purple bubble, and a drag over the hours opens New event on the time dragged (Calem,
  // report 8fcbe013, 2nd opening: "When you hover over the block where you would like to book an appointment, show that
  // block with a purple bubble (for that hour) and allow the user to click and drag the time frame that they prefer 30
  // minutes or longer than 1 hour"). The 1st opening only let one click on an hour open New event, hour:00 to hour+1:00,
  // with nothing on screen to say which hour that would be. The bubble wears the all-day cards' purple wash and line
  // (cardLine), with an event's 3px sides and 6px corners. A drag snaps to half hours and runs from the half hour it began
  // in to the one the mouse is in, up or down, never under half an hour; a click that never leaves its half hour still
  // opens the whole hour. Esc drops a drag. Only a mouse drags: a finger still scrolls the grid, and a tap opens its hour.
  const [hover, setHover] = useState<{ day: number; hour: number } | null>(null);
  const dragRef = useRef<HourDrag | null>(null);   // the drag as the pointer handlers last left it, read by the next one
  const [drag, setDrag] = useState<HourDrag | null>(null);   // the same, as drawn
  const lastPointer = useRef('');                  // what last pressed on the hours: 'mouse', 'touch' or 'pen'
  const slotAt = (y: number, el: Element) => Math.max(0, Math.min(47, Math.floor((y - el.getBoundingClientRect().top) * 2 / pxh)));
  const dropDrag = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null; setDrag(null); setHover(null);
    // Let the mouse go, or after Esc or a right-click menu the day would still be handed every move.
    if (d && d.el.hasPointerCapture(d.id)) d.el.releasePointerCapture(d.id);
  }, []);
  const pressHours = (e: React.PointerEvent<HTMLDivElement>, day: number, ds: string) => {
    lastPointer.current = e.pointerType;
    if (e.pointerType !== 'mouse' || e.button !== 0 || (e.target as Element).closest('button')) return;
    const el = e.currentTarget, s = slotAt(e.clientY, el);
    try { el.setPointerCapture(e.pointerId); } catch { /* the drag still follows the mouse inside the day */ }
    dragRef.current = { day, ds, from: s, to: s, moved: false, el, id: e.pointerId };
    setDrag(dragRef.current);
  };
  const follow = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const s = slotAt(e.clientY, d.el);
    if (s === d.to) return;
    dragRef.current = { ...d, to: s, moved: true };
    setDrag(dragRef.current);
  };
  const moveOverHours = (e: React.PointerEvent<HTMLDivElement>, day: number) => {
    if (dragRef.current) { follow(e); return; }
    if (e.pointerType !== 'mouse') return;
    // No bubble over an event: a click there opens the event.
    const hour = (e.target as Element).closest('button') ? -1 : Math.max(0, Math.min(23, Math.floor((e.clientY - e.currentTarget.getBoundingClientRect().top) / pxh)));
    setHover(p => (hour < 0 ? null : p && p.day === day && p.hour === hour ? p : { day, hour }));
  };
  const releaseHours = (e: React.PointerEvent) => {
    follow(e);
    const d = dragRef.current;
    if (!d) return;
    dropDrag();
    if (!d.moved) onNew(d.ds, `${pad(Math.floor(d.from / 2))}:00`);
    else onNew(d.ds, slotTime(Math.min(d.from, d.to)), slotEnd(Math.max(d.from, d.to) + 1));
  };
  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dropDrag(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dragging, dropDrag]);
  const bubble: React.CSSProperties = { position: 'absolute', left: 3, right: 3, borderRadius: 6, background: EMAIL_CAL_GROUP.tint, boxShadow: `inset 0 0 0 1px ${cardLine}`, pointerEvents: 'none' };
  return (
    // overflowX hidden (owner 2026-08-26): the all-day pills are nowrap — without clamping, they
    // inflate the 1fr grid tracks past the viewport and the whole band becomes horizontally
    // draggable, tearing every pill away from its date column.
    // overflowAnchor none (report ae0a0d20): the browser would otherwise shift the scroll itself as the band grows,
    // leaving the band over the hour that was under it and looking to onScroll like the user had scrolled.
    <div ref={boxRef} onScroll={onScroll} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', overflowAnchor: 'none' }}>
      {/* Day headers — sticky; same scroll container as the grid so columns line up */}
      <div style={{ display: 'grid', gridTemplateColumns: cols, position: 'sticky', top: 0, zIndex: 7, background: 'var(--bg-surface)', height: headH, borderBottom: '1px solid var(--border)', padding: `0 ${side}px` }}>
        <div />
        {days.map((d, i) => {
          const isToday = ymd(d) === today;
          return <div key={i} onClick={() => !single && onDay(ymd(d))} style={{ padding: '5px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, borderLeft: '1px solid var(--border)', cursor: single ? 'default' : 'pointer', overflow: 'hidden' }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{WD[d.getDay()]}</div>
            {/* The date, today's on a circle: a flex box round the figures trimmed to their cap height, so they sit in the
                circle's middle (see the Month grid's day number), where a 30px line set them on DM Sans's line box.
                The circle is the Calendar's purple, as the Month grid's is (report 2f61c80a, 2nd opening). */}
            <div style={{ fontSize: 18, fontWeight: 800, color: isToday ? '#fff' : 'var(--text-primary)', background: isToday ? tabColor('calendar') : 'transparent', borderRadius: 999, width: 30, height: 30, lineHeight: '30px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{d.getDate()}</span>
            </div>
          </div>;
        })}
      </div>
      {/* All-day band — sticky under the header, one grid row per section (owner 2026-09-18).
          Documents and birthdays sit folded on top; the job-related work is open beneath them
          (Calem 2026-09-24). Each section is exactly as tall as its busiest day: open, every pill of
          that day shows; folded, one row of counts. No height cap — the old one (rows × 26 + 10, at
          most 200) allowed 26px a pill where a pill and its gap take 31, and the day cells silently
          cut the last event off with no scrollbar to reach it (Calem 2026-09-24, screenshot-4).
          Each section is a bubble of its own (Calem 2026-09-30, report e84845c3: "Make each section a
          bubble. Also keep them collapsible as they are already"), like the Contacts cards and Active
          Jobs' UNKNOWN FINISH DATE bubble, where they were plain rows split by a 1px grey line. A card
          is dressed as the top menu's Email / Contacts / Calendar box: its wash, laid on --bg-surface as
          the box's is so it is the very same colour, inside a purple line four shades darker than the
          box's (reports 0b462a24, efa40753, ddf1ba0e, 0fb815da — see cardLine), with 10px corners. The cards stand 7px apart,
          where it was 4 (Calem, report 5d3e1bce: "On all system pages (except tracking) increase the gaps between the
          bubbles by 3 pixels"), with 4px still
          over the first and under the last, and 12px in from either side (reports 0b462a24, efa40753 — see side),
          on the page's colour, which the band is: white, --bg-surface, since the page went white (Calem,
          report ddf1ba0e, 3rd opening), where it was the page's grey, --c-gray-50, and --bg-surface before
          that. The page shows between them and round their corners, and no timed event scrolling up under
          the band shows through. The line is an inset shadow, not a border, and a card has no margin or padding of its
          own — either would take width from the card's grid alone, and its days would no longer line up
          with the day names above and the hours below (report 257a251e). The 12px is the band's padding,
          and the header and the time grid take the same. A card is never under 40px tall, where it was 28 (report
          f1696a06, 2026-10-04: "Make the bubbles for the three categories taller vertically"): a folded card is 40, and
          so is an open one whose busiest day has a single pill; an open one with more grows with its pills, as it did.
          Folded, the label and each day's count sit half-way down the card, and the count half-way across its day too
          (same report: "And center the circular amount of notification"), where both were at the top and the count at
          the day's left; open, the label and every day's pills start at the TOP, so the first pill of each day sits on
          one line across the whole card (report 07a113fe — see the day column). Folding works as it did. */}
      <div ref={bandRef} style={{ position: 'sticky', top: headH, zIndex: 6, background: 'var(--bg-surface)', borderBottom: '2px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 7, padding: secRows.length ? `4px ${side}px` : 0 }}>
        {secRows.map(sec => {
          const open = openSec[sec.key] !== false;
          return (
            <div key={sec.key} style={{ display: 'grid', gridTemplateColumns: cols, minHeight: 40, borderRadius: 10, backgroundColor: 'var(--bg-surface)', backgroundImage: `linear-gradient(${EMAIL_CAL_GROUP.tint}, ${EMAIL_CAL_GROUP.tint})`, boxShadow: `inset 0 0 0 1px ${cardLine}` }}>
              <button onClick={() => toggleSec(sec.key)} title={t(open ? 'cal_sec_hide' : 'cal_sec_show')}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '5px 6px 5px 3px', display: 'flex', alignItems: open ? 'flex-start' : 'center', justifyContent: 'flex-end', gap: 2, fontSize: 10.5, fontWeight: 700, color: 'var(--text-tertiary)', textAlign: 'right', lineHeight: 1.5 }}>
                <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }} />
                <span style={{ minWidth: 0, overflow: 'hidden' }}>{t(sec.labelKey)}</span>
              </button>
              {days.map((d, i) => (
                // An OPEN section's pills start at the TOP of the day (report 07a113fe, P0: "Maintain vertical
                // alignment on all fields"). They were centred, by report 17b67403 ("Center the notifications
                // shown below vertically in the provided space") — but a card is exactly as tall as its busiest
                // day, so centring moved every quieter day's pills DOWN by half the room it was not using: a day
                // with one pill sat lower than the day beside it with two, and the first pill of each day landed
                // on a different line. Top-aligned, every day's first pill is on one line and each following pill
                // on the next, which is the alignment that report asked for in the first place.
                // FOLDED, the count bubble keeps its centring (report f1696a06, "center the circular amount of
                // notification"): a folded card is one 40px row with a single bubble in it, so there is nothing
                // to line that bubble up with and half-way down is where it belongs.
                <div key={i} style={{ borderLeft: '1px solid var(--border)', padding: 3, display: 'flex', flexDirection: 'column', justifyContent: open ? 'flex-start' : 'center', alignItems: open ? 'stretch' : 'center', gap: 3, minWidth: 0, overflow: 'hidden' }}>
                  {open ? sec.perDay[i].map((e, j) => {
                    const c = dispColor(e);
                    // An expiry that already has a job on it is drawn struck through (the
                    // strike-through rule at jobMatchFor). The pill is untouched otherwise: same
                    // outline, same white, same size and place; only its words are crossed.
                    const done = struck(e);
                    // The client has refused this one (report e4ed287f): the pill's white becomes the
                    // Void tag's light red and its words the tag's red, and the strikethrough stands
                    // down. Its outline, its size and its place are untouched.
                    const vd = refused(e);
                    // The pill is the event's own colour drawn as a line all round it, with plain white inside
                    // (Calem, report 17b67403: "Remove the colored background of each notification and instead
                    // put the dark border (in this case orange) around the notification. Remove the extra border
                    // thickness on the left-hand side of the notification at the same time"; then, 2nd opening,
                    // "Make the notification bubble background white"). It had that colour washed over it at 13%
                    // (c + '22') behind a 3px bar down its left edge and no line of its own; the 1st opening took
                    // both away but left the pill see-through, so the card's own wash showed through it and it
                    // never read as white. Its fill is now --bg-surface, the band's own white round the cards —
                    // the same white the folded count bubble on this very band already wears (report 2f61c80a,
                    // 3rd opening), which had been see-through for exactly the same reason. So an Extension reads
                    // as orange words on white inside an orange outline. The title was already in the event's own
                    // colour (Calem 2026-09-24), and nothing about the pill's size or place moves: it keeps the
                    // 1px line, the '1px 9px' padding that pays for that line above and below (1 + 1 + the 13px
                    // text's 1.7 line + 1 + 1), the 5px corners and the 10px the text stands in from either end,
                    // so a day still fits as many as it did, now from the top of the card down. The Month
                    // grid's cell pills keep the wash and the bar — see there.
                    return (
                      <button key={j} onClick={() => onEvent(e)} onContextMenu={vd ? voidMenu(vd) : undefined} title={vd ? voidTip(vd, t) : done ? jobTip(done, t) : undefined} style={{ flexShrink: 0, display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'left', border: `1px solid ${c}`, borderRadius: 5, padding: '1px 9px', background: vd ? VOID_BG : 'var(--bg-surface)', color: vd ? VOID_FG : c, cursor: 'pointer', fontSize: 13, lineHeight: 1.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: !vd && done ? 'line-through' : undefined }}>{e.source === 'jobReminder' && <i className="ti ti-bell-ringing" style={{ color: c, marginRight: 4 }} />}{e.summary}</button>
                    );
                  }) : (
                    // Folded: the count still shows, so nobody has to remember the section is there. Its bubble is
                    // dressed as the top menu's Email / Contacts / Calendar tab (Calem 2026-09-30, report 7d1198d1: "the
                    // outer border in purple and the number in purple inside, with a light purple background"):
                    // menuPill's unselected look, as the Month / Week / Day pills wear — the box's purple line, now
                    // solid, and the purple for the number. That line has since gone one shade darker than the box's
                    // (report eb5e4682, 4th opening: "Make the circle border one shade darker"): 0.33 → 0.50, a quarter
                    // of the way on to the solid #7F77DD, the step darken in tabPalette counts one, #D5D2F4 → #BFBBEE
                    // on white (worked out, not measured on screen). It is the line the cards had after report
                    // 0b462a24. And a shade darker again (report 0fb815da: "Make the bubble border one shade darker for
                    // everything on the purple menu pages"): 0.50 → 0.63, a quarter of the way on from there, #AEA9EA on
                    // white — a shade at a time, not darken(…, 2), which would go half-way (see darken). That is a shade
                    // on from the toolbar's unselected pills, which took the 0.50 then (pageLine), and two lighter than
                    // the cards' (see cardLine); the box keeps its own. Its fill is plain white, --bg-surface, the
                    // band's own white round the cards (report 2f61c80a, 3rd opening: "Change the notification bubble
                    // background to white for birthdays/ID/job-related"), where it was the card's own wash three
                    // shades lighter, taken a shade at a time (report f1696a06, 2nd opening: "make the background of
                    // the notification circle one shade lighter"; then report eb5e4682, 2nd and 3rd openings): 0.094 →
                    // 0.07 → 0.05 → 0.04, #FAFAFE
                    // on white, so pale that a shade moved red, green and blue only one or two steps in 255. White,
                    // not see-through: the 1st opening of f1696a06 had taken its fill, the box's pale wash, away
                    // ("Remove the background from the circle"), so the card's wash showed through it. Was a dashed
                    // grey ring round a grey number, with no fill. It sits in the middle of its day, not pinned to the
                    // top left (report f1696a06 — see the band).
                    // It is a true circle, 22 across and 22 down with its line (report 2f61c80a, 3rd opening: "make
                    // each circle 2 px larger"), 20 before (report eb5e4682, 2nd opening: "Make the notifications
                    // circles (not squashed circles)"), where 9px of padding either side of the figures and 1px over
                    // and under had made it a pill about 26 by 18. No padding, and a least width, not a width, as the
                    // top menu's count bubbles have, so the one or two figures a day shows sit in the circle and three
                    // would widen it rather than spill over its line. The folded card is 40 tall (report f1696a06 —
                    // see the band), so 22 still fits with room. Its corners are as they were, and the figures keep
                    // their 11px.
                    // The number sits in the middle of the bubble itself, across and down (Bugify #836d4443, 2nd
                    // opening: "Check all other circles in the system and make sure of the same - centered vertically
                    // and horizontally"): a flex box round the figures trimmed to their cap height, as the top menu's
                    // count bubbles have, where they sat on DM Sans's line box. The trimmed figures do not give the
                    // bubble its height, so it is set — 22 now, 20 before that, and 18 first: the 11px figures' 14px
                    // line with the 1px padding and the 1px line above and below (worked out from DM Sans's line
                    // height, not measured on screen).
                    sec.perDay[i].length > 0 && (
                      <button onClick={() => toggleSec(sec.key)} style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', minWidth: 22, height: 22, border: '1px solid', borderRadius: 999, cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: 0, whiteSpace: 'nowrap', ...menuPill('calendar', false), borderColor: darken(darken(EMAIL_CAL_GROUP.border, 1), 1), background: 'var(--bg-surface)' }}>
                        <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{sec.perDay[i].length}</span>
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {/* Time grid — scrolls in the same container; headers above stay pinned */}
      <div style={{ display: 'grid', gridTemplateColumns: cols, position: 'relative', padding: `0 ${side}px` }}>
        <div>
          {HOURS.map(h => <div key={h} style={{ height: pxh, fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'right', paddingRight: 7, transform: 'translateY(-7px)' }}>{h ? `${pad(h)}:00` : ''}</div>)}
        </div>
        {days.map((d, i) => {
          const ds = ymd(d);
          // The span being dragged on this day, in half hours, once the drag has left the half hour it began in.
          const range = drag && drag.day === i && drag.moved ? [Math.min(drag.from, drag.to), Math.max(drag.from, drag.to) + 1] : null;
          // No text is picked up as a drag sweeps over the day (Safari reads only the -webkit- one).
          return (
            <div key={i} onPointerDown={e => pressHours(e, i, ds)} onPointerMove={e => moveOverHours(e, i)} onPointerUp={releaseHours}
              onPointerCancel={dropDrag} onContextMenu={dropDrag} onPointerLeave={() => setHover(null)}
              style={{ position: 'relative', borderLeft: '1px solid var(--border)', minWidth: 0, userSelect: 'none', WebkitUserSelect: 'none' }}>
              {/* One click on an hour opens New event at that hour (Calem, report 8fcbe013: "Allow users to click on the
                  time block to create a new event"); it took a double-click. A mouse's click, like its drag, is read by the
                  day's pointer handlers (2nd opening), so an hour itself answers only a tap or a pen, and one press never
                  opens the form twice. The events are drawn over the hours, not inside them, and pressing on one starts
                  no drag, so a click on an event still opens that event alone. */}
              {HOURS.map(h => <div key={h} ref={i === 0 && h === DAY_START ? startRef : undefined} onClick={() => { if (lastPointer.current !== 'mouse') onNew(ds, `${pad(h)}:00`); }} style={{ height: pxh, borderBottom: '1px solid var(--border)' }} />)}
              {/* The hour under the mouse, drawn before the events so they sit on top of it and still open on a click. */}
              {hover && hover.day === i && !range && <div style={{ ...bubble, top: hover.hour * pxh, height: pxh }} />}
              {timedByDay[i].map((e, j) => {
                const top = (mins(e.time) / 60) * pxh;
                const end = e.endTime ? mins(e.endTime) : mins(e.time) + 60;
                const h = Math.max(26, ((end - mins(e.time)) / 60) * pxh);
                const c = dispColor(e);
                return (
                  <button key={j} onClick={() => onEvent(e)} title={e.summary}
                    style={{ position: 'absolute', top, left: 3, right: 3, height: h, textAlign: 'left', border: 'none', borderRadius: 6, padding: '3px 7px', background: c + '33', borderLeft: `3px solid ${c}`, color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, overflow: 'hidden', lineHeight: 1.5 }}>
                    <b style={{ color: c }}>{e.time}</b> {e.summary}
                  </button>
                );
              })}
              {/* The span being dragged, with its times, drawn over the events so the times can always be read. */}
              {range && (
                <div style={{ ...bubble, top: range[0] * pxh / 2, height: (range[1] - range[0]) * pxh / 2, padding: '2px 7px', fontSize: 11.5, fontWeight: 700, lineHeight: '16px', color: EMAIL_CAL_GROUP.color, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  {slotTime(range[0])} – {slotEnd(range[1])}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailModal({ e, t, onClose, onEdit, onDeleted }: { e: CalEvent; t: any; onClose: () => void; onEdit: () => void; onDeleted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);   // recurring → ask "this" vs "all future"
  const doDelete = async (scope: 'all' | 'this' | 'future') => {
    setBusy(true); setConfirmDel(false);
    try { alertGuestWarning(await api.deleteCalendarEvent(e.email, e.href, scope, e.date), t); onDeleted(); }
    catch { alert('Delete failed'); setBusy(false); }
  };
  const del = () => {
    if (e.recurring) { setConfirmDel(true); return; }              // recurring → Apple-style choice
    if (window.confirm(t('cal_delete_confirm'))) doDelete('all');
  };
  const row = (icon: string, content: React.ReactNode) => content ? <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', display: 'flex', gap: 8, marginBottom: 7 }}><i className={`ti ti-${icon}`} style={{ marginTop: 2 }} /><span style={{ whiteSpace: 'pre-wrap' }}>{content}</span></div> : null;
  const isRem = e.source === 'jobReminder';
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={ev => ev.stopPropagation()} style={{ width: 'min(460px, 96vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ width: 12, height: 12, borderRadius: 4, background: e.color }} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: e.color }}>{e.person}</span>
          {e.recurring && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}><i className="ti ti-repeat" /></span>}
          <button onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" style={{ fontSize: 18 }} /></button>
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>{e.summary}</div>
        {row('calendar', `${e.date}${!e.allDay ? ` · ${e.time}${e.endTime ? '–' + e.endTime : ''}` : ` · ${t('cal_all_day')}`}`)}
        {e.recurring && row('repeat', t('cal_repeats', { freq: t('cal_repeat_' + (e.repeat || 'yearly').toLowerCase()) }))}
        {e.alert && row('bell', `${t('cal_alert')}: ${t(ALERT_KEY[e.alert] || 'cal_alert')}`)}
        {row('map-pin', e.location)}
        {row('users', e.attendees.length ? e.attendees.join(', ') : null)}
        {row('user-check', e.organizer && `${t('cal_organizer')}: ${e.organizer}`)}
        {row('align-left', e.description)}
        {row('flag', e.status && e.status !== 'CONFIRMED' ? e.status : null)}
        {isRem && row('briefcase', `Job #${e.jobNo}`)}
        {isRem && row('calendar-due', `${t('cal_rem_due')}: ${e.dueDate}`)}
        {isRem && row('mail', e.emailTo && e.emailTo.length ? `${t('cal_rem_emailto')}: ${e.emailTo.join(', ')}` : null)}
        {isRem ? (
          <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--text-tertiary)', background: 'var(--bg-app)', border: '1px dashed var(--border-strong)', borderRadius: 9, padding: '9px 12px' }}>
            <i className="ti ti-bell-ringing" style={{ marginRight: 6 }} />{t('cal_rem_hint')}
          </div>
        ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={onEdit} style={{ flex: 1, padding: '9px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}><i className="ti ti-pencil" /> {t('cal_edit')}</button>
          <button onClick={del} disabled={busy} style={{ flex: 1, padding: '9px', borderRadius: 9, border: '1px solid #E24B4A', background: '#FDECEC', color: '#C0392B', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}><i className="ti ti-trash" /> {t('cal_delete')}</button>
        </div>
        )}
      </div>

      {confirmDel && (
        <div onClick={ev => { ev.stopPropagation(); setConfirmDel(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
          <div onClick={ev => ev.stopPropagation()} style={{ width: 'min(360px, 94vw)', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: '0 10px 40px rgba(0,0,0,0.3)', textAlign: 'center' }}>
            <div style={{ fontSize: 15.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 7 }}>{t('cal_delete_recurring_title')}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 18 }}>{t('cal_delete_recurring_desc')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              <button onClick={() => doDelete('this')} disabled={busy} style={{ padding: '11px', borderRadius: 10, border: 'none', background: '#E24B4A', color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>{t('cal_delete_this')}</button>
              <button onClick={() => doDelete('future')} disabled={busy} style={{ padding: '11px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-primary)', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>{t('cal_delete_future')}</button>
              <button onClick={() => setConfirmDel(false)} style={{ padding: '11px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Department visits (Calem 2026-09-23, report 7565dcb5) ───────────────────────────────────────
// When somebody goes to a government office, the form records WHICH office and WHICH active job(s)
// the trip is for. These events live on the CalDAV server, which holds nothing but title, dates,
// location, description, repeat and alert — so both facts ride in plain text that every phone
// calendar shows as-is and that readDeptVisit can take apart again when the event is edited:
//   Location     "Immigration" · "Immigration — Maenam" · "Other: Provincial Court"
//   Description  … then a last line "Related jobs: #61290 John Smith — 90 Day Report; #61301 …"
// The saved words stay English whatever the screen language: they are data the next reader, and
// this parser, must recognise. Only the dropdown's labels are translated.
const DEPARTMENTS: Array<{ name: string; labelKey: string }> = [
  { name: 'Immigration',        labelKey: 'cal_dept_immigration' },
  { name: 'Labor Department',   labelKey: 'cal_dept_labor' },
  { name: 'Tessaban',           labelKey: 'cal_dept_tessaban' },
  { name: 'Land Office',        labelKey: 'cal_dept_land' },
  { name: 'Social Security',    labelKey: 'cal_dept_sso' },
  { name: 'Revenue Department', labelKey: 'cal_dept_revenue' },
  { name: 'DBD',                labelKey: 'cal_dept_dbd' },
];
const DEPT_OTHER = 'Other';
// "Other" keeps a prefix in the saved location. A bare "Provincial Court" could not be told apart
// from an ordinary location once the event is opened again.
const OTHER_PREFIX = 'Other:';
const LOC_SEP = ' — ';
const JOBS_PREFIX = 'Related jobs:';
const jobNoOf = (label: string) => /^#(\d+)/.exec(label)?.[1] || '';
/** "#61290 John Smith — 90 Day Report". The job number leads so the line can be read back. */
function jobLabel(j: any): string {
  const who = [j.first_name, j.last_name].filter(Boolean).join(' ').trim() || String(j.client_company || '').trim();
  const what = String(j.specific_job || '').trim();
  // No ';' inside a label: it separates one job from the next on the saved line.
  return `#${j.job_no}${who ? ' ' + who : ''}${what ? ' — ' + what : ''}`.replace(/;/g, ',').replace(/\s+/g, ' ');
}
/** The same line as the picker and its chips show it: the client's company without its legal form
 *  (Calem, report fcc6685f). Display only — the event keeps the line exactly as jobLabel wrote it. */
function jobLabelShown(label: string): string {
  const m = /^(#\d+ )(.*?)( — .*)?$/.exec(label);
  return m ? m[1] + displayCompanyName(m[2]) + (m[3] || '') : label;
}

/** A saved event split back into the form's fields. Anything that is not recognisably a department
 *  visit comes back untouched — location and description exactly as stored — so opening and saving
 *  an ordinary event can never rewrite it. */
function readDeptVisit(location: string, description: string): { dept: string; other: string; place: string; jobs: string[]; description: string } {
  const loc = location.trim();
  const low = loc.toLowerCase();
  let dept = '', other = '', place = loc;
  const preset = DEPARTMENTS.find(d => low === d.name.toLowerCase() || low.startsWith(d.name.toLowerCase() + LOC_SEP));
  if (preset) { dept = preset.name; place = loc.slice(preset.name.length + LOC_SEP.length).trim(); }
  else if (low.startsWith(OTHER_PREFIX.toLowerCase())) {
    const rest = loc.slice(OTHER_PREFIX.length);
    const i = rest.indexOf(LOC_SEP);
    other = (i < 0 ? rest : rest.slice(0, i)).trim();
    if (other) { dept = DEPT_OTHER; place = i < 0 ? '' : rest.slice(i + LOC_SEP.length).trim(); }
  }
  if (!dept) return { dept: '', other: '', place: loc, jobs: [], description };
  // Only a line whose every item is a "#<job no> …" is taken as ours; anything else someone typed
  // after "Related jobs:" stays in the description as written.
  const itemsOf = (line: string): string[] | null => {
    const s = line.trim();
    if (!s.toLowerCase().startsWith(JOBS_PREFIX.toLowerCase())) return null;
    const items = s.slice(JOBS_PREFIX.length).split(/\s*;\s*(?=#\d)/).map(x => x.trim());
    return items.every(x => jobNoOf(x)) ? items : null;
  };
  const jobs: string[] = [];
  const kept: string[] = [];
  for (const line of description.split(/\r?\n/)) {
    const items = itemsOf(line);
    if (!items) { kept.push(line); continue; }
    for (const it of items) if (!jobs.some(x => jobNoOf(x) === jobNoOf(it))) jobs.push(it);
  }
  return { dept, other, place, jobs, description: kept.join('\n').trim() };
}

function EventForm({ initial, accounts, t, onClose, onSaved }: { initial: Partial<CalEvent>; accounts: CalAccount[]; t: any; onClose: () => void; onSaved: () => void }) {
  const editing = !!initial.href;
  const [email, setEmail] = useState(initial.email || accounts[0]?.email || '');
  const [summary, setSummary] = useState(initial.summary || '');
  // Unticked unless the event is itself all-day (Calem, report 8fcbe013); it was ticked whenever no hour came with it.
  const [allDay, setAllDay] = useState(initial.allDay ?? false);
  const [date, setDate] = useState(initial.date || ymd(new Date()));
  const [endDate, setEndDate] = useState(initial.endDate || initial.date || ymd(new Date()));
  const [time, setTime] = useState(initial.time || '09:00');
  const [endTime, setEndTime] = useState(initial.endTime || addHour(initial.time || '09:00'));
  const [dv0] = useState(() => readDeptVisit(initial.location || '', initial.description || ''));
  const [location, setLocation] = useState(dv0.place);
  const [description, setDescription] = useState(dv0.description);
  const [dept, setDept] = useState(dv0.dept);
  const [deptOther, setDeptOther] = useState(dv0.other);
  const [jobs, setJobs] = useState<string[]>(dv0.jobs);
  const [repeat, setRepeat] = useState(initial.repeat || '');
  const [alert, setAlert] = useState(initial.alert || '');
  const [guests, setGuests] = useState<string[]>(initial.guests || []);
  // Several addresses pasted at once become one chip each.
  const addGuests = (v: string) => setGuests(p => Array.from(new Set([...p, ...v.split(/[\s,;]+/).map(x => x.trim().toLowerCase()).filter(Boolean)])));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // An hour or a Month day opens this form on one click now (Calem, report 8fcbe013). Whoever still double-clicks, the
  // way it opened before, lands the second click on the form the moment it is up: on the dimmed page round it, which
  // shuts it, or on All day, a list or a button. The rest of the clicks that opened the form are let go; the next new
  // click (a click count of 1) ends that, and from then on every click works as it always has.
  const opening = useRef(true);
  const passOpening = (e: React.MouseEvent) => {
    if (e.detail < 2) opening.current = false;
    else if (opening.current) { e.preventDefault(); e.stopPropagation(); }
  };

  const save = async () => {
    if (!summary.trim()) { setErr(t('cal_need_title')); return; }
    if (dept === DEPT_OTHER && !deptOther.trim()) { setErr(t('cal_dept_other_need')); return; }
    const badGuests = guests.filter(g => !GUEST_RE.test(g));
    if (badGuests.length) { setErr(t('cal_guests_bad', { list: badGuests.join(', ') })); return; }
    setBusy(true); setErr('');
    // A department visit saves the department as the location (whatever is typed under Location
    // follows it) and the picked jobs as the description's last line. With no department picked,
    // both fields are saved exactly as typed, as before.
    const deptName = dept === DEPT_OTHER ? `${OTHER_PREFIX} ${deptOther.trim()}` : dept;
    const loc = [deptName, location.trim()].filter(Boolean).join(LOC_SEP);
    const desc = [description.trim(), dept && jobs.length ? `${JOBS_PREFIX} ${jobs.join('; ')}` : ''].filter(Boolean).join('\n\n');
    // An event read before the calendar knew about guests carries no list; left empty, the server
    // keeps whatever the event has rather than taking everyone off it.
    const sendGuests = editing && !initial.guests && !guests.length ? undefined : guests;
    const body: any = { email, summary: summary.trim(), date, endDate: allDay ? endDate : date, allDay, time: allDay ? '' : time, endTime: allDay ? '' : endTime, location: loc, description: desc, repeat, alert, guests: sendGuests };
    try {
      // Changing the calendar while editing = a MOVE: the server creates the copy on the target
      // account and deletes the source (owner 2026-08-26). `email` must stay the ORIGINAL so the
      // server can find the event; the picked calendar rides in `newEmail`.
      if (editing) {
        const r: any = await api.updateCalendarEvent({ ...body, email: initial.email, newEmail: email, href: initial.href });
        if (r?.warning) window.alert(r.warning);
        alertGuestWarning(r, t);
      }
      else alertGuestWarning(await api.createCalendarEvent(body), t);
      onSaved();
    } catch { setErr('Save failed'); setBusy(false); }
  };

  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };
  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13.5, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 9, color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
  return (
    <div onMouseDownCapture={passOpening} onClickCapture={passOpening} onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(480px, 96vw)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 16 }}>{editing ? t('cal_edit_event') : t('cal_new_event')}</div>
        <div style={{ marginBottom: 12 }}><label style={lbl}>{t('cal_title')}</label><input value={summary} onChange={e => setSummary(e.target.value)} style={inp} autoFocus /></div>
        <div style={{ marginBottom: 12 }}><label style={lbl}>{t('cal_person')}</label>
          <select value={email} onChange={e => setEmail(e.target.value)} style={inp}>
            {accounts.map(a => <option key={a.email} value={a.email}>{a.label}</option>)}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13.5, color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} /> {t('cal_allday')}
        </label>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          {/* Owner 2026-08-26: an EXISTING event's dates are pinned — birthdays / expiries / team
              events must not drift by a casual edit. A real reschedule = delete + recreate. */}
          <div style={{ flex: 1, minWidth: 130 }}><label style={lbl}>{t('cal_start')}</label><input type="date" value={date} onChange={e => setDate(e.target.value)} disabled={editing} title={editing ? t('cal_date_locked') : undefined} style={{ ...inp, opacity: editing ? 0.6 : 1 }} /></div>
          {!allDay && <div style={{ width: 110 }}><label style={lbl}>&nbsp;</label><input type="time" value={time} onChange={e => setTime(e.target.value)} style={inp} /></div>}
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 130 }}><label style={lbl}>{t('cal_end')}</label><input type="date" value={allDay ? endDate : date} onChange={e => setEndDate(e.target.value)} disabled={!allDay || editing} title={editing ? t('cal_date_locked') : undefined} style={{ ...inp, opacity: allDay && !editing ? 1 : 0.6 }} /></div>
          {!allDay && <div style={{ width: 110 }}><label style={lbl}>&nbsp;</label><input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={inp} /></div>}
        </div>
        {/* Guests (Calem, report c8aa8f3f): one or more email addresses, typed or picked from Contacts.
            Each is emailed the invitation, any change or cancellation, and a reminder at the Alert time.
            They sit under the person and the times (Calem, report 8fcbe013: "move the guests up to underneath the
            user and the time"), before Repeat; they were near the bottom, under Location. */}
        <div style={{ marginBottom: 12 }}><label style={lbl}>{t('cal_guests')}</label>
          <div style={{ ...inp, padding: '2px 11px' }}>
            <EmailTagInput bare label={t('cal_guests')} values={guests} onAdd={addGuests} onRemove={g => setGuests(p => p.filter(x => x !== g))} placeholder={t('cal_guests_ph')} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cal_guests_hint')}</div>
        </div>
        <div style={{ marginBottom: 12 }}><label style={lbl}>{t('cal_repeat')}</label>
          <select value={repeat} onChange={e => setRepeat(e.target.value)} style={inp}>
            <option value="">{t('cal_repeat_none')}</option>
            <option value="DAILY">{t('cal_repeat_daily')}</option>
            <option value="WEEKLY">{t('cal_repeat_weekly')}</option>
            <option value="MONTHLY">{t('cal_repeat_monthly')}</option>
            <option value="YEARLY">{t('cal_repeat_yearly')}</option>
          </select>
          {editing && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}><i className="ti ti-lock" style={{ marginRight: 4 }} aria-hidden />{t('cal_date_locked')}</div>}
          {editing && (initial.repeat || '') && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cal_repeat_edit_hint')}</div>}
        </div>
        <div style={{ marginBottom: 12 }}><label style={lbl}>{t('cal_alert')}</label>
          <select value={alert} onChange={e => setAlert(e.target.value)} style={inp}>
            <option value="">{t('cal_alert_none')}</option>
            <option value="0">{t('cal_alert_attime')}</option>
            <option value="10">{t('cal_alert_10m')}</option>
            <option value="30">{t('cal_alert_30m')}</option>
            <option value="60">{t('cal_alert_1h')}</option>
            <option value="1440">{t('cal_alert_1d')}</option>
            <option value="10080">{t('cal_alert_1w')}</option>
            <option value="20160">{t('cal_alert_2w')}</option>
            <option value="40320">{t('cal_alert_4w')}</option>
            <option value="60480">{t('cal_alert_6w')}</option>
          </select>
        </div>
        <div style={{ marginBottom: 12 }}><label style={lbl}>{t('cal_dept')}</label>
          <select value={dept} onChange={e => setDept(e.target.value)} style={inp}>
            <option value="">{t('cal_dept_none')}</option>
            {DEPARTMENTS.map(d => <option key={d.name} value={d.name}>{t(d.labelKey)}</option>)}
            <option value={DEPT_OTHER}>{t('cal_dept_other')}</option>
          </select>
          {dept === DEPT_OTHER && <input value={deptOther} onChange={e => setDeptOther(e.target.value)} placeholder={t('cal_dept_other_ph')} style={{ ...inp, marginTop: 6 }} />}
          {dept && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('cal_dept_hint')}</div>}
        </div>
        {dept && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ ...lbl, textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>{t('cal_dept_jobs')}</label>
            <DeptJobsPicker value={jobs} onChange={setJobs} t={t} inp={inp} />
          </div>
        )}
        <div style={{ marginBottom: 12 }}><label style={lbl}>{t('cal_location')}</label><input value={location} onChange={e => setLocation(e.target.value)} style={inp} /></div>
        <div style={{ marginBottom: 14 }}><label style={lbl}>{t('cal_desc')}</label><textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} /></div>
        {err && <div style={{ color: '#C0392B', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cal_cancel')}</button>
          <button onClick={save} disabled={busy} style={{ flex: 2, padding: '10px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? t('cal_saving') : t('cal_save')}</button>
        </div>
      </div>
    </div>
  );
}

// The active jobs a department visit is for. Reads the same list as the Active Jobs page —
// view=active, scope=any (every team, the auto-generated tax jobs included, never Sandbox) —
// searched by job no. / client, so what can be ticked here is exactly what is open there. A pick is
// held as its label because that label is what the event stores (see readDeptVisit); a job linked
// earlier and closed since still shows as a chip and can be removed.
function DeptJobsPicker({ value, onChange, t, inp }: { value: string[]; onChange: (v: string[]) => void; t: any; inp: React.CSSProperties }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [errKey, setErrKey] = useState('');
  useEffect(() => {
    let alive = true;
    const h = setTimeout(() => {
      setLoading(true);
      api.getJobs({ view: 'active', scope: 'any', q: q.trim(), limit: 25 })
        .then((r: any) => { if (alive) { setRows(r?.rows || []); setErrKey(''); } })
        // /api/jobs sits behind the Active/Closed Jobs modules: a calendar user without them gets 403.
        .catch((e: any) => { if (alive) { setRows([]); setErrKey(e?.status === 403 ? 'cal_dept_jobs_noaccess' : 'cal_dept_jobs_err'); } })
        .finally(() => { if (alive) setLoading(false); });
    }, 250);
    return () => { alive = false; clearTimeout(h); };
  }, [q]);
  const picked = value.map(jobNoOf);
  const toggle = (j: any) => {
    const no = String(j.job_no);
    onChange(picked.includes(no) ? value.filter(l => jobNoOf(l) !== no) : [...value, jobLabel(j)]);
  };
  const note = (text: string, color = 'var(--text-tertiary)') => <div style={{ padding: '9px 11px', fontSize: 12.5, color }}>{text}</div>;
  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 7 }}>
          {value.map(l => (
            <span key={jobNoOf(l) || l} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 8, background: 'var(--bg-active)', color: 'var(--accent)' }}>
              {jobLabelShown(l)}
              <span onClick={() => onChange(value.filter(x => x !== l))} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>
            </span>
          ))}
        </div>
      )}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('cal_dept_jobs_search')} style={inp} />
      <div style={{ marginTop: 6, maxHeight: 190, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 9 }}>
        {loading && !rows.length ? note(t('cal_loading'))
          : errKey ? note(t(errKey), '#C0392B')
          : !rows.length ? note(t('cal_dept_jobs_none'))
          : rows.map((j: any) => (
            <label key={j.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 12.5 }}>
              <input type="checkbox" checked={picked.includes(String(j.job_no))} onChange={() => toggle(j)} style={{ cursor: 'pointer' }} />
              <span style={{ fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap' }}>#{j.job_no}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>{jobLabelShown(jobLabel(j)).replace(/^#\d+\s*/, '')}</span>
            </label>
          ))}
      </div>
    </div>
  );
}
